import { getValidToken, validateAndRefreshToken } from '../utils/tokenUtils';
import { apiConfig } from '../config';
import { store } from '../store';
import { logoutUser } from '../store/authSlice';
import { CompetitorAdvantage as CompAdvantage, CompetitorWeakness as CompWeakness } from '../types/competitor';
import {
  normalizePlaybook,
  normalizePlaybookOption,
  type SalesPlaybook,
  type SalesPlaybookOption,
  type SalesPlaybookPitchBullet,
  type SalesPlaybookQuestion,
  type SalesPlaybookRole,
  type SalesPlaybookTag,
} from '../types/playbook';

export interface ApiResponse<T = any> {
  success: boolean;
  data?: T;
  message?: string;
  error?: string;
}

export interface ApiError {
  success: false;
  error: string;
  message: string;
  code?: string;
}

const apiFailureAsError = (failure: unknown, fallback: string): Error => {
  if (failure instanceof Error) return failure;
  if (failure && typeof failure === 'object') {
    const apiError = failure as Partial<ApiError>;
    return new Error(apiError.error || apiError.message || fallback);
  }
  return new Error(fallback);
};

export interface PaginationInfo {
  page: number;
  limit: number;
  total: number;
  totalPages: number;
}

export interface DictionaryVariation {
  _id: string;
  question: string;
  category?: string;
  needsEmbedding?: boolean;
  hasEmbedding?: boolean;
}

export interface DictionaryItem {
  _id: string;
  question: string;
  answer: string;
  category?: string;
  isOriginal?: boolean;
  parentId?: string | null;
  variations?: DictionaryVariation[];
  variationCount?: number;
  hasEmbedding?: boolean;
  needsEmbedding?: boolean;
  createdAt?: string;
  updatedAt?: string;
  version?: number;
  /** Provenance — present on the live document but not always populated for
   *  older rows. Purely informational in the UI. */
  // 'csm-suggested' and 'reviewer-suggested' are synonyms — see FAQ.js /
  // Objection.js for the deprecation TODO.
  source?: 'manual' | 'voice' | 'playbook' | 'knowledge-generator' | 'csm-suggested' | 'reviewer-suggested' | string;
  /** Only present on /dictionary/search results (type=all mixes objections
   *  and FAQs, so each hit needs to say which it is). */
  type?: 'objection' | 'faq';
}

export interface ObjectionsResponse {
  objections: DictionaryItem[];
  pagination: PaginationInfo | null;
}

export interface FAQsResponse {
  faqs: DictionaryItem[];
  pagination: PaginationInfo | null;
}

export interface SearchResponse {
  success: boolean;
  results: DictionaryItem[];
  pagination: PaginationInfo;
  error?: string;
}

export interface AnswersGroupedResponse {
  answers: Array<DictionaryItem & { kind: 'objections' | 'faqs' }>;
  pagination: PaginationInfo;
}

export interface KnowledgePolishField {
  id: string;
  text: string;
  contentRole?: 'agent-talk-track' | 'customer-utterance';
}

export interface KnowledgePolishResult extends KnowledgePolishField {
  changed: boolean;
}

export type KnowledgePolishContentType = 'faq' | 'objection' | 'competitor' | 'playbook';

export interface PublishedSearchReadiness {
  status: 'ready' | 'indexing' | 'error' | 'unavailable';
  expected?: number;
  ready?: number;
  pending?: number;
  failed?: number;
}

export interface PublishedKnowledgeMatch {
  id: string;
  parentId?: string | null;
  question: string;
  answer: string;
  type: 'objection' | 'faq';
  category?: string;
  relevanceScore?: number;
}

export type KbReadinessCategoryStatus = 'ready' | 'needs_review' | 'needs_more_detail' | 'not_set_up';

export interface KbReadinessSummary {
  thresholds: Record<string, number>;
  publishedKnowledge: {
    hasAnswers: boolean;
    hasLandmines: boolean;
    hasCompetitors: boolean;
    hasOrganizationContext: boolean;
    hasAny: boolean;
  };
  companyContext: { hasWhatYouSell: boolean; hasHowItWorks: boolean };
  answers: {
    objections: { total: number; meeting: number; withoutVariations: number; cleanupPending: number };
    faqs: { total: number; meeting: number; withoutVariations: number; cleanupPending: number };
    total: number;
    meeting: number;
    cleanupPending: number;
    /** Computed server-side — see kbController.js's answersStatus. The
     * frontend renders this directly rather than re-deriving it from the
     * counts above, so the two cannot drift on what counts as "ready". */
    status: KbReadinessCategoryStatus;
  };
  landmines: {
    total: number;
    meeting: number;
    pending: number;
    missingSafeGuidance?: number;
    missingDetractorTip?: number;
    status: KbReadinessCategoryStatus;
  };
  competitors: { total: number; meeting: number; status: KbReadinessCategoryStatus };
  playbooks: { total: number; meeting: number; status: KbReadinessCategoryStatus };
}

export type KbAuditFindingKind = 'duplicate_family' | 'filler_variations' | 'broad_landmine' | 'contradiction';

export interface KbAuditSourceItem {
  sourceCollection: 'objections' | 'faqs' | 'landmines';
  documentId: string;
  label: string;
  answer: string;
  variations?: string[];
  version: string;
}

export interface KbAuditSourceEdit {
  documentId: string;
  question?: string;
  answer?: string;
  trigger?: string;
  triggerVariations?: string[];
}

export interface KbAuditFinding {
  _id: string;
  kind: KbAuditFindingKind;
  confidence: 'high' | 'medium' | 'uncertain';
  status: 'open' | 'dismissed' | 'applied' | 'superseded' | 'apply_failed';
  title: string;
  impact: string;
  recommendation: string;
  sourceItems: KbAuditSourceItem[];
  proposedChange: {
    combinedAnswer?: string;
    recommendedType?: 'objection' | 'faq';
    removeVariations?: string[];
    trigger?: string;
    triggerVariations?: string[];
  };
  evidence: { totalFires: number; distinctCalls: number };
  createdAt: string;
}

export interface KbAuditDecision {
  findingId: string;
  action: 'merge' | 'edit' | 'delete' | 'dismiss' | 'keep_distinct' | 'mark_intentional';
  documentId?: string;
  keepDocumentId?: string;
  resultingType?: 'objection' | 'faq';
  question?: string;
  answer?: string;
  variations?: string[];
  removeVariations?: string[];
  trigger?: string;
  triggerVariations?: string[];
  sourceEdits?: KbAuditSourceEdit[];
}

/** One thumbs up/down on a session's detected item (same contract as the live apps). */
export interface EntryFeedbackPayload {
  entryType: 'conversation' | 'landmine' | 'competitor';
  entryTimestamp: string;
  category?: string;
  rating: 'up' | 'down';
  reason?: string;
}

/**
 * The backend answers 200 even when nothing matched (`{sessionId, userId: caller}`
 * plus the item's timestamp): only `persisted: true` means the rating was written.
 */
export interface EntryFeedbackResponse extends ApiResponse {
  persisted?: boolean;
}

class ApiService {
  private baseURL: string;
  
  constructor() {
    this.baseURL = apiConfig.baseURL;
    console.debug('[ApiService] Initialized with base URL:', this.baseURL);
  }

  private async getAuthHeaders(): Promise<Record<string, string>> {
    const token = await getValidToken();
    const headers: Record<string, string> = {
      'Content-Type': 'application/json',
    };
    
    if (token) {
      headers['Authorization'] = `Bearer ${token}`;
      console.debug('[ApiService] Added auth header with token (length:', token.length, ')');
    } else {
      console.debug('[ApiService] No auth token available');
    }
    
    return headers;
  }

  private async handleResponse<T>(response: Response, retryCount: number = 0): Promise<T> {
    const contentType = response.headers.get('content-type');
    let data: any;
    
    if (contentType && contentType.includes('application/json')) {
      data = await response.json();
    } else {
      data = await response.text();
    }
    
    console.debug('[ApiService] Response:', {
      status: response.status,
      statusText: response.statusText,
      url: response.url,
      data: data
    });
    
    // Handle 401 Unauthorized errors
    if (response.status === 401 && retryCount === 0) {
      console.warn('[ApiService] 401 Unauthorized, attempting token refresh...');
      
      // Try to refresh the token
      const { isValid } = await validateAndRefreshToken();
      
      if (isValid) {
        console.debug('[ApiService] Token refreshed successfully, retrying request...');
        // Token was refreshed, retry the request once
        // Note: The original request will be retried by the calling method
        const error: ApiError = {
          success: false,
          error: 'TOKEN_REFRESHED',
          message: 'Token was refreshed, retry the request'
        };
        throw error;
      } else {
        console.error('[ApiService] Token refresh failed, logging out user...');
        // Token refresh failed, log out the user
        store.dispatch(logoutUser());
        
        // Redirect to login page
        if (typeof window !== 'undefined' && window.location.pathname !== '/login') {
          window.location.href = '/login';
        }
        
        const error: ApiError = {
          success: false,
          error: 'AUTHENTICATION_FAILED',
          message: 'Your session has expired. Please log in again.'
        };
        throw error;
      }
    }
    
    if (!response.ok) {
      const error: ApiError = {
        success: false,
        error: data.error || 'API_ERROR',
        message: data.message || `HTTP ${response.status}: ${response.statusText}`
      };
      if (data.code) error.code = data.code;
      
      console.error('[ApiService] API Error:', error);
      throw error;
    }
    
    return data;
  }

  async get<T = any>(endpoint: string, params?: Record<string, string>, retryCount: number = 0): Promise<ApiResponse<T>> {
    try {
      const url = new URL(`${this.baseURL}/api${endpoint}`);
      
      if (params) {
        Object.entries(params).forEach(([key, value]) => {
          if (value !== undefined && value !== null) {
            url.searchParams.append(key, value);
          }
        });
      }
      
      console.debug('[ApiService] GET request:', url.toString());
      
      const headers = await this.getAuthHeaders();
      
      const response = await fetch(url.toString(), {
        method: 'GET',
        headers,
      });
      
      return await this.handleResponse<ApiResponse<T>>(response, retryCount);
    } catch (error: any) {
      // If token was refreshed, retry the request once
      if (error.error === 'TOKEN_REFRESHED' && retryCount === 0) {
        console.debug('[ApiService] Retrying GET request after token refresh...');
        return this.get<T>(endpoint, params, 1);
      }
      console.error('[ApiService] GET request failed:', error);
      throw error;
    }
  }

  async post<T = any>(endpoint: string, data?: any, retryCount: number = 0): Promise<ApiResponse<T>> {
    try {
      const url = `${this.baseURL}/api${endpoint}`;

      console.debug('[ApiService] POST request:', url, 'with data:', data);

      const headers = await this.getAuthHeaders();

      const response = await fetch(url, {
        method: 'POST',
        headers,
        body: data !== undefined ? JSON.stringify(data) : undefined,
      });

      return await this.handleResponse<ApiResponse<T>>(response, retryCount);
    } catch (error: any) {
      // If token was refreshed, retry the request once
      if (error.error === 'TOKEN_REFRESHED' && retryCount === 0) {
        console.debug('[ApiService] Retrying POST request after token refresh...');
        return this.post<T>(endpoint, data, 1);
      }
      console.error('[ApiService] POST request failed:', error);
      throw error;
    }
  }

  async postFormData<T = any>(endpoint: string, formData: FormData, retryCount: number = 0): Promise<ApiResponse<T>> {
    try {
      const url = `${this.baseURL}/api${endpoint}`;

      console.debug('[ApiService] POST FormData request:', url);

      const token = await getValidToken();
      const headers: Record<string, string> = {};

      if (token) {
        headers['Authorization'] = `Bearer ${token}`;
        console.debug('[ApiService] Added auth header for FormData upload');
      }

      // Don't set Content-Type for FormData - browser will set it with boundary

      const response = await fetch(url, {
        method: 'POST',
        headers,
        body: formData,
      });

      return await this.handleResponse<ApiResponse<T>>(response, retryCount);
    } catch (error: any) {
      // If token was refreshed, retry the request once
      if (error.error === 'TOKEN_REFRESHED' && retryCount === 0) {
        console.debug('[ApiService] Retrying FormData POST request after token refresh...');
        return this.postFormData<T>(endpoint, formData, 1);
      }
      console.error('[ApiService] POST FormData request failed:', error);
      throw error;
    }
  }

  async putFormData<T = any>(endpoint: string, formData: FormData, retryCount: number = 0): Promise<ApiResponse<T>> {
    try {
      const url = `${this.baseURL}/api${endpoint}`;

      console.debug('[ApiService] PUT FormData request:', url);

      const token = await getValidToken();
      const headers: Record<string, string> = {};

      if (token) {
        headers['Authorization'] = `Bearer ${token}`;
        console.debug('[ApiService] Added auth header for FormData upload');
      }

      // Don't set Content-Type for FormData - browser will set it with boundary

      const response = await fetch(url, {
        method: 'PUT',
        headers,
        body: formData,
      });

      return await this.handleResponse<ApiResponse<T>>(response, retryCount);
    } catch (error: any) {
      // If token was refreshed, retry the request once
      if (error.error === 'TOKEN_REFRESHED' && retryCount === 0) {
        console.debug('[ApiService] Retrying FormData PUT request after token refresh...');
        return this.putFormData<T>(endpoint, formData, 1);
      }
      console.error('[ApiService] PUT FormData request failed:', error);
      throw error;
    }
  }

  async patchFormData<T = any>(endpoint: string, formData: FormData, retryCount: number = 0): Promise<ApiResponse<T>> {
    try {
      const url = `${this.baseURL}/api${endpoint}`;

      console.debug('[ApiService] PATCH FormData request:', url);

      const token = await getValidToken();
      const headers: Record<string, string> = {};

      if (token) {
        headers['Authorization'] = `Bearer ${token}`;
        console.debug('[ApiService] Added auth header for FormData upload');
      }

      // Don't set Content-Type for FormData - browser will set it with boundary

      const response = await fetch(url, {
        method: 'PATCH',
        headers,
        body: formData,
      });

      return await this.handleResponse<ApiResponse<T>>(response, retryCount);
    } catch (error: any) {
      // If token was refreshed, retry the request once
      if (error.error === 'TOKEN_REFRESHED' && retryCount === 0) {
        console.debug('[ApiService] Retrying FormData PATCH request after token refresh...');
        return this.patchFormData<T>(endpoint, formData, 1);
      }
      console.error('[ApiService] PATCH FormData request failed:', error);
      throw error;
    }
  }

  async put<T = any>(endpoint: string, data?: any, retryCount: number = 0): Promise<ApiResponse<T>> {
    try {
      const url = `${this.baseURL}/api${endpoint}`;
      
      console.debug('[ApiService] PUT request:', url, 'with data:', data);
      
      const headers = await this.getAuthHeaders();
      
      const response = await fetch(url, {
        method: 'PUT',
        headers,
        body: data !== undefined ? JSON.stringify(data) : undefined,
      });
      
      return await this.handleResponse<ApiResponse<T>>(response, retryCount);
    } catch (error: any) {
      // If token was refreshed, retry the request once
      if (error.error === 'TOKEN_REFRESHED' && retryCount === 0) {
        console.debug('[ApiService] Retrying PUT request after token refresh...');
        return this.put<T>(endpoint, data, 1);
      }
      console.error('[ApiService] PUT request failed:', error);
      throw error;
    }
  }

  async patch<T = any>(endpoint: string, data?: any, retryCount: number = 0): Promise<ApiResponse<T>> {
    try {
      const url = `${this.baseURL}/api${endpoint}`;

      console.debug('[ApiService] PATCH request:', url, 'with data:', data);

      const headers = await this.getAuthHeaders();

      const response = await fetch(url, {
        method: 'PATCH',
        headers,
        body: data !== undefined ? JSON.stringify(data) : undefined,
      });

      return await this.handleResponse<ApiResponse<T>>(response, retryCount);
    } catch (error: any) {
      // If token was refreshed, retry the request once
      if (error.error === 'TOKEN_REFRESHED' && retryCount === 0) {
        console.debug('[ApiService] Retrying PATCH request after token refresh...');
        return this.patch<T>(endpoint, data, 1);
      }
      console.error('[ApiService] PATCH request failed:', error);
      throw error;
    }
  }

  async delete<T = any>(endpoint: string, retryCount: number = 0): Promise<ApiResponse<T>> {
    try {
      const url = `${this.baseURL}/api${endpoint}`;
      
      console.debug('[ApiService] DELETE request:', url);
      
      const headers = await this.getAuthHeaders();
      
      const response = await fetch(url, {
        method: 'DELETE',
        headers,
      });
      
      return await this.handleResponse<ApiResponse<T>>(response, retryCount);
    } catch (error: any) {
      // If token was refreshed, retry the request once
      if (error.error === 'TOKEN_REFRESHED' && retryCount === 0) {
        console.debug('[ApiService] Retrying DELETE request after token refresh...');
        return this.delete<T>(endpoint, 1);
      }
      console.error('[ApiService] DELETE request failed:', error);
      throw error;
    }
  }

  // Post-call feedback on a session. Both routes are owner-only: they match
  // {sessionId, userId: caller}, so only the session's own rep can write.
  async submitEntryFeedback(sessionId: string, payload: EntryFeedbackPayload): Promise<EntryFeedbackResponse> {
    return this.patch(`/conversations/sessions/${sessionId}/entry-feedback`, payload) as Promise<EntryFeedbackResponse>;
  }

  async submitSessionFeedback(sessionId: string, formData: FormData): Promise<ApiResponse> {
    return this.patchFormData(`/conversations/sessions/${sessionId}/general-feedback`, formData);
  }

  // Health check without auth
  async healthCheck(): Promise<ApiResponse> {
    try {
      const url = `${this.baseURL}/api/health`;
      
      console.debug('[ApiService] Health check:', url);
      
      const response = await fetch(url, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
        },
      });
      
      return await this.handleResponse<ApiResponse>(response);
    } catch (error) {
      console.error('[ApiService] Health check failed:', error);
      throw error;
    }
  }
}

// Create and export singleton instance
export const apiService = new ApiService();

// Export common API endpoints
export const authApi = {
  getCurrentUser: () => apiService.get('/auth/me'),
  updateProfile: (data: any) => apiService.put('/auth/profile', data),
  getUserStats: () => apiService.get('/auth/stats'),
  deleteAccount: () => apiService.delete('/auth/account'),
};

export const transcriptApi = {
  getTranscripts: (params?: Record<string, string>) => apiService.get('/transcripts', params),
  getTranscript: (id: string) => apiService.get(`/transcripts/${id}`),
  createTranscript: (data: any) => apiService.post('/transcripts', data),
  updateTranscript: (id: string, data: any) => apiService.put(`/transcripts/${id}`, data),
  deleteTranscript: (id: string) => apiService.delete(`/transcripts/${id}`),
  getTranscriptStats: () => apiService.get('/transcripts/stats'),
};

export const settingsApi = {
  getSettings: () => apiService.get('/settings'),
};

export interface ActionItem {
  _id: string;
  type: string;
  title: string;
  body?: string;
  ctaLabel?: string;
  ctaLink?: string;
  status: 'open' | 'dismissed' | 'completed';
  createdAt: string;
}

export const actionsApi = {
  getActions: () => apiService.get<{ actions: ActionItem[] }>('/actions'),
  getActionsCount: () => apiService.get<{ count: number }>('/actions/count'),
  updateActionStatus: (id: string, status: 'dismissed' | 'completed') =>
    apiService.patch<{ action: ActionItem }>(`/actions/${id}`, { status }),
  /** Marks the caller's own open action(s) of a type completed, without
   *  needing a specific action _id — for a flow (like onboarding) to clear
   *  its own Actions Center entry once it actually finishes. */
  completeActionsByType: (type: string) =>
    apiService.patch<{ modifiedCount: number }>('/actions/complete-by-type', { type }),
};

// Dictionary API endpoints with pagination
export const getDictionary = async (page: number = 1, limit: number = 20, category?: string) => {
  try {
    const params: Record<string, string> = {
      page: page.toString(),
      limit: limit.toString()
    };
    if (category) {
      params.category = category;
    }
    const response = await apiService.get('/dictionary', params);
    return response?.data || null;
  } catch (error) {
    console.error('[Dictionary API] Error fetching dictionary:', error);
    return null;
  }
};

export const getAnswersGrouped = async (
  page: number = 1,
  limit: number = 20,
  query: string = ''
): Promise<AnswersGroupedResponse> => {
  const response = await apiService.get<any>('/dictionary/answers/grouped', {
    page: page.toString(),
    limit: limit.toString(),
    ...(query.trim() ? { q: query.trim() } : {}),
  });
  const data = response as any;
  return {
    answers: data?.answers || [],
    pagination: data?.pagination || { page, limit, total: 0, totalPages: 1 },
  };
};

export const getPublishedSearchReadiness = async (): Promise<PublishedSearchReadiness> => {
  const response = await apiService.get<PublishedSearchReadiness>('/dictionary/live-search/readiness');
  if (!response.data) throw new Error('Published search readiness was not returned');
  return response.data;
};

export const searchPublishedKnowledge = async (
  query: string
): Promise<{ match: PublishedKnowledgeMatch | null }> => {
  const response = await apiService.post<{ match: PublishedKnowledgeMatch | null }>(
    '/dictionary/live-search',
    { query }
  );
  return { match: response.data?.match ?? (response as any).match ?? null };
};

export const getKbReadinessSummary = async (): Promise<KbReadinessSummary> => {
  const response = await apiService.get<KbReadinessSummary>('/kb/readiness-summary');
  if (!response.data) throw new Error('Readiness summary was not returned');
  return response.data;
};

export const getKbAuditFindings = async (): Promise<{ findings: KbAuditFinding[]; openCount: number; lastRunAt: string | null }> => {
  const response = await apiService.get<{ findings: KbAuditFinding[]; openCount: number; lastRunAt: string | null }>('/kb/audit/findings');
  if (!response.data) throw new Error('KB audit findings were not returned');
  return response.data;
};

export const applyKbAuditDecisions = async (decisions: KbAuditDecision[]): Promise<{ findingId: string; status: string }[]> => {
  const response = await apiService.post<{ results: { findingId: string; status: string }[] }>('/kb/audit/apply', { decisions });
  return response.data?.results || [];
};

export const getKbAuditApplyStatus = async (findingIds: string[]): Promise<{
  items: Array<{ findingId: string; status: 'indexing' | 'live' | 'error'; error?: string }>;
  allLive: boolean;
}> => {
  const response = await apiService.get<{
    items: Array<{ findingId: string; status: 'indexing' | 'live' | 'error'; error?: string }>;
    allLive: boolean;
  }>('/kb/audit/apply-status', { findingIds: findingIds.join(',') });
  if (!response.data) throw new Error('Live guidance status was not returned');
  return response.data;
};

export const getObjectionsGrouped = async (page: number = 1, limit: number = 20, category?: string): Promise<ObjectionsResponse> => {
  try {
    const params: Record<string, string> = {
      page: page.toString(),
      limit: limit.toString()
    };
    if (category) {
      params.category = category;
    }
    const response = await apiService.get<any>('/dictionary/objections/grouped', params);
    // The response is an ApiResponse, but the backend sends the data directly
    // Cast to any first to avoid type errors
    const data = response as any;
    return {
      objections: data?.objections || [],
      pagination: data?.pagination || null
    };
  } catch (error) {
    console.error('[Dictionary API] Error fetching grouped objections:', error);
    return { objections: [], pagination: null };
  }
};

export const getObjections = async (page: number = 1, limit: number = 20, category?: string): Promise<ObjectionsResponse> => {
  try {
    const params: Record<string, string> = {
      page: page.toString(),
      limit: limit.toString()
    };
    if (category) {
      params.category = category;
    }
    const response = await apiService.get<any>('/dictionary/objections', params);
    // The response is an ApiResponse, but the backend sends the data directly
    // Cast to any first to avoid type errors
    const data = response as any;
    return {
      objections: data?.objections || [],
      pagination: data?.pagination || null
    };
  } catch (error) {
    console.error('[Dictionary API] Error fetching objections:', error);
    return { objections: [], pagination: null };
  }
};

export const getFAQs = async (page: number = 1, limit: number = 20, category?: string): Promise<FAQsResponse> => {
  try {
    const params: Record<string, string> = {
      page: page.toString(),
      limit: limit.toString()
    };
    if (category) {
      params.category = category;
    }
    const response = await apiService.get<any>('/dictionary/faqs', params);
    // The response is an ApiResponse, but the backend sends the data directly
    // Cast to any first to avoid type errors
    const data = response as any;
    return {
      faqs: data?.faqs || [],
      pagination: data?.pagination || null
    };
  } catch (error) {
    console.error('[Dictionary API] Error fetching FAQs:', error);
    return { faqs: [], pagination: null };
  }
};

export const addObjection = async (question: string, answer: string, category?: string, isOriginal?: boolean, parentId?: string | null) => {
  try {
    const response = await apiService.post('/dictionary/objections', {
      question,
      answer,
      category: category || 'Original',
      isOriginal: isOriginal || false,
      parentId: parentId || null
    });
    console.log('the response is', response)
    return response || null;
  } catch (error) {
    console.error('[Dictionary API] Error adding objection:', error);
    throw error; // Re-throw to handle in component
  }
};

/** Returns a preview only. Callers place the proposal into an editor; this API never saves it. */
export const polishKnowledgeFields = async (
  contentType: KnowledgePolishContentType,
  items: KnowledgePolishField[],
  protectedSpans: string[] = []
): Promise<KnowledgePolishResult[]> => {
  try {
    const response = await apiService.post<{ items: KnowledgePolishResult[] }>(
      '/dictionary/polish',
      { contentType, items, protectedSpans }
    );
    const responseItems = response.data?.items;
    if (response.success && Array.isArray(responseItems)) return responseItems;
    throw new Error(response?.error || 'AI polishing returned an invalid response');
  } catch (error) {
    console.error('[Dictionary API] Error polishing knowledge:', error);
    throw error;
  }
};

export const addObjectionVariation = async (parentId: string, question: string) => {
  try {
    const response = await apiService.post(`/dictionary/objections/${parentId}/variations`, {
      question
      // Don't send category for variations - only originals have topic categories
    });
    return response?.data || null;
  } catch (error) {
    console.error('[Dictionary API] Error adding variation:', error);
    throw error;
  }
};

export const updateObjection = async (id: string, data: { question?: string; answer?: string; category?: string }) => {
  try {
    const response = await apiService.put(`/dictionary/objections/${id}`, data);
    return response?.data || null;
  } catch (error) {
    console.error('[Dictionary API] Error updating objection:', error);
    throw error; // Re-throw to handle in component
  }
};

export const deleteObjection = async (id: string) => {
  try {
    const response = await apiService.delete(`/dictionary/objections/${id}`);
    return response?.data || null;
  } catch (error) {
    console.error('[Dictionary API] Error deleting objection:', error);
    throw error; // Re-throw to handle in component
  }
};

export const getFAQsGrouped = async (page: number = 1, limit: number = 20, category?: string): Promise<FAQsResponse> => {
  try {
    const params: Record<string, string> = {
      page: page.toString(),
      limit: limit.toString()
    };
    if (category) {
      params.category = category;
    }
    const response = await apiService.get<any>('/dictionary/faqs/grouped', params);
    const data = response as any;
    return {
      faqs: data?.faqs || [],
      pagination: data?.pagination || null
    };
  } catch (error) {
    console.error('Error fetching grouped FAQs:', error);
    return { faqs: [], pagination: null };
  }
};

/** Search published objections and FAQs in the authenticated client database. */
export const searchDictionary = async (
  query: string,
  type: 'objection' | 'faq' | 'all' = 'all',
  page: number = 1,
  limit: number = 20
): Promise<SearchResponse> => {
  try {
    const params: Record<string, string> = {
      q: query,
      type,
      page: page.toString(),
      limit: limit.toString(),
    };

    const response = await apiService.get<SearchResponse>('/dictionary/search', params);
    return response as unknown as SearchResponse;
  } catch (error: any) {
    console.error('[Search API] Search error:', error);
    throw new Error(error?.message || 'Search failed');
  }
};

export const addFAQVariation = async (parentId: string, question: string) => {
  try {
    const response = await apiService.post(`/dictionary/faqs/${parentId}/variations`, {
      question
      // Don't send category for variations - only originals have topic categories
    });
    return response?.data || null;
  } catch (error) {
    console.error('[Dictionary API] Error adding FAQ variation:', error);
    throw error;
  }
};

export const addFAQ = async (question: string, answer: string, category?: string) => {
  try {
    const response = await apiService.post('/dictionary/faqs', {
      question,
      answer,
      category: category || 'Original'
    });
    return response || null;
  } catch (error) {
    console.error('[Dictionary API] Error adding FAQ:', error);
    throw error; // Re-throw to handle in component
  }
};

export const updateFAQ = async (id: string, data: { question?: string; answer?: string; category?: string }) => {
  try {
    const response = await apiService.put(`/dictionary/faqs/${id}`, data);
    return response?.data || null;
  } catch (error) {
    console.error('[Dictionary API] Error updating FAQ:', error);
    throw error; // Re-throw to handle in component
  }
};

export const deleteFAQ = async (id: string) => {
  try {
    const response = await apiService.delete(`/dictionary/faqs/${id}`);
    return response?.data || null;
  } catch (error) {
    console.error('[Dictionary API] Error deleting FAQ:', error);
    throw error; // Re-throw to handle in component
  }
};

// Generate variations for an objection using AI (returns variations without saving)
export const generateObjectionVariations = async (objectionId: string, count: number = 3) => {
  try {
    const response = await apiService.post(`/dictionary/objections/${objectionId}/generate-variations`, {
      count
    });
    return response;
  } catch (error) {
    console.error('[Dictionary API] Error generating objection variations:', error);
    throw error;
  }
};

// Generate variations for a FAQ using AI (returns variations without saving)
export const generateFAQVariations = async (faqId: string, count: number = 3) => {
  try {
    const response = await apiService.post(`/dictionary/faqs/${faqId}/generate-variations`, {
      count
    });
    return response;
  } catch (error) {
    console.error('[Dictionary API] Error generating FAQ variations:', error);
    throw error;
  }
};

// Check for similar questions in the database
export const checkSimilarQuestions = async (question: string, type: 'objection' | 'faq' | 'both' = 'both', threshold: number = 0.5) => {
  try {
    const response = await apiService.post('/dictionary/check-similar', {
      question,
      type,
      threshold
    });
    return response;
  } catch (error) {
    console.error('[Dictionary API] Error checking similar questions:', error);
    throw error;
  }
};

// Landmine Management API
export interface LandmineItem {
  _id: string;
  trigger: string;
  triggerVariations: string[];
  landmineType: 'legal_risk' | 'detractor';
  category?: string;  // Required only for legal_risk type
  priority: 'high' | 'medium' | 'low';
  canSay?: Array<{
    point: string;
    context?: string;
  }>;
  cannotSay?: Array<{
    point: string;
    reason?: string;
    severity?: 'legal_risk' | 'compliance_risk' | 'brand_risk' | 'general';
  }>;
  isActive: boolean;
  quickTips?: string;
  /** AI-drafted landmines start 'pending' and are excluded from live
   *  detection until approved; human-entered ones default to 'approved'.
   *  Optional because older rows predate the field. */
  approvalStatus?: 'approved' | 'pending';
  createdAt?: string;
  updatedAt?: string;
  createdBy?: string;
  updatedBy?: string;
}

// Competitor interfaces - re-export from types
export type CompetitorAdvantage = CompAdvantage;
export type CompetitorWeakness = CompWeakness;

export interface CompetitorItem {
  _id: string;
  name: string;
  // Note: variations, regex_patterns, and metadata removed - detection is now LLM-based
  advantages: CompetitorAdvantage[];
  weaknesses: CompetitorWeakness[];
  talking_points: string;
  category: 'direct_competitor' | 'indirect_competitor' | 'alternative_solution';
  priority: 'high' | 'medium' | 'low';
  isActive: boolean;
  createdAt?: string;
  updatedAt?: string;
}

export interface CompetitorsResponse {
  competitors: CompetitorItem[];
  pagination: PaginationInfo | null;
}

export interface LandminesResponse {
  landmines: LandmineItem[];
  pagination?: PaginationInfo;
}

// Get all landmines with pagination
export const getLandmines = async (
  page: number = 1,
  limit: number = 20,
  filters?: { category?: string; priority?: string; isActive?: boolean; landmineType?: string }
): Promise<{ landmines: LandmineItem[]; pagination: PaginationInfo | null }> => {
  try {
    const params = new URLSearchParams();
    params.append('page', page.toString());
    params.append('limit', limit.toString());
    if (filters?.category) params.append('category', filters.category);
    if (filters?.priority) params.append('priority', filters.priority);
    if (filters?.isActive !== undefined) params.append('isActive', filters.isActive.toString());
    if (filters?.landmineType) params.append('landmineType', filters.landmineType);

    const url = `/landmines?${params.toString()}`
    const response = await apiService.get<any>(url) as any;
    // Handle new response format with success field and pagination
    console.log('the response is', response)
    if (response?.success) {
      return {
        landmines: response.landmines || [],
        pagination: response.pagination || null
      };
    }
    // Fallback for old format
    return {
      landmines: [],
      pagination: null
    };
  } catch (error) {
    console.error('[Landmine API] Error getting landmines:', error);
    throw error;
  }
};

// Get landmines grouped by category
export const getLandminesGrouped = async (filters?: { category?: string; priority?: string; isActive?: boolean }): Promise<any> => {
  try {
    const params = new URLSearchParams();
    if (filters?.category) params.append('category', filters.category);
    if (filters?.priority) params.append('priority', filters.priority);
    if (filters?.isActive !== undefined) params.append('isActive', filters.isActive.toString());
    
    const response = await apiService.get<any>(`/landmines/grouped?${params.toString()}`) as any;
    // Handle new response format with success field
    if (response?.success && response?.landmines) {
      return response.landmines;
    }
    // Fallback for direct response
    if (response && typeof response === 'object' && !response.success) {
      return response;
    }
    return {};
  } catch (error) {
    console.error('[Landmine API] Error getting grouped landmines:', error);
    throw error;
  }
};

// Get single landmine by ID
export const getLandmineById = async (id: string): Promise<LandmineItem | null> => {
  try {
    const response = await apiService.get<any>(`/landmines/${id}`) as any;
    // Handle new response format with success field - single item uses 'landmine' field
    if (response?.success && response?.landmine) {
      return response.landmine;
    }
    // Fallback for direct response
    if (response && typeof response === 'object' && !response.success) {
      return response as any;
    }
    return null;
  } catch (error) {
    console.error('[Landmine API] Error getting landmine:', error);
    throw error;
  }
};

// Add new landmine
export const addLandmine = async (landmineData: Partial<LandmineItem>): Promise<LandmineItem> => {
  try {
    const response = await apiService.post<any>('/landmines', landmineData) as any;
    // Handle new response format with success field - create uses 'landmine' field
    if (response?.success && response?.landmine) {
      return response.landmine;
    }
    // Fallback for direct response
    if (response && typeof response === 'object' && !response.success) {
      return response as any;
    }
    throw new Error('Invalid response format');
  } catch (error) {
    console.error('[Landmine API] Error adding landmine:', error);
    throw error;
  }
};

// Update landmine
export const updateLandmine = async (id: string, updates: Partial<LandmineItem>): Promise<LandmineItem> => {
  try {
    const response = await apiService.put<any>(`/landmines/${id}`, updates) as any;
    // Handle new response format with success field - update uses 'landmine' field
    if (response?.success && response?.landmine) {
      return response.landmine;
    }
    // Fallback for direct response
    if (response && typeof response === 'object' && !response.success) {
      return response as any;
    }
    throw new Error('Invalid response format');
  } catch (error) {
    console.error('[Landmine API] Error updating landmine:', error);
    throw error;
  }
};

// Review is deliberately separate from generic update: the backend binds the
// approval to the exact safety-relevant content the admin displayed.
export const reviewLandmine = async (id: string, reviewedVersion: string): Promise<LandmineItem> => {
  const response = await apiService.post<any>(`/landmines/${id}/review`, { reviewedVersion }) as any;
  if (response?.success && response?.landmine) return response.landmine;
  throw new Error(response?.error || 'Failed to review landmine');
};

// Delete landmine
export const deleteLandmine = async (id: string): Promise<void> => {
  try {
    const response = await apiService.delete<any>(`/landmines/${id}`) as any;
    // The delete endpoint returns success with a message and landmine field
    // We don't need to return anything, just ensure it succeeded
    if (!response?.success) {
      throw new Error(response?.error || 'Failed to delete landmine');
    }
  } catch (error) {
    console.error('[Landmine API] Error deleting landmine:', error);
    throw error;
  }
};

// Add trigger variation
export const addLandmineTriggerVariation = async (id: string, variation: string): Promise<LandmineItem> => {
  try {
    const response = await apiService.post<any>(`/landmines/${id}/variations`, { variation }) as any;
    // Handle new response format with success field - variations uses 'landmine' field
    if (response?.success && response?.landmine) {
      return response.landmine;
    }
    // Fallback for direct response
    if (response && typeof response === 'object' && !response.success) {
      return response as any;
    }
    throw new Error('Invalid response format');
  } catch (error) {
    console.error('[Landmine API] Error adding trigger variation:', error);
    throw error;
  }
};

// Remove trigger variation
export const removeLandmineTriggerVariation = async (id: string, variation: string): Promise<LandmineItem> => {
  try {
    // Since DELETE with body is not standard, we'll use a custom request
    const token = await getValidToken();
    const headers: Record<string, string> = {
      'Content-Type': 'application/json',
    };
    
    if (token) {
      headers['Authorization'] = `Bearer ${token}`;
    }
    
    const response = await fetch(`${apiConfig.baseURL}/api/landmines/${id}/variations`, {
      method: 'DELETE',
      headers,
      body: JSON.stringify({ variation }),
    });
    
    const result = await response.json();
    if (!response.ok) {
      throw new Error(result.error || 'Failed to remove trigger variation');
    }
    
    // Handle new response format with success field - uses 'landmine' field (singular)
    if (result?.success && result?.landmine) {
      return result.landmine;
    }
    // Fallback for backward compatibility
    return result;
  } catch (error) {
    console.error('[Landmine API] Error removing trigger variation:', error);
    throw error;
  }
};

// Toggle landmine active status
export const toggleLandmineStatus = async (id: string): Promise<LandmineItem> => {
  try {
    // Use custom PATCH request
    const token = await getValidToken();
    const headers: Record<string, string> = {
      'Content-Type': 'application/json',
    };
    
    if (token) {
      headers['Authorization'] = `Bearer ${token}`;
    }
    
    const response = await fetch(`${apiConfig.baseURL}/api/landmines/${id}/toggle-status`, {
      method: 'PATCH',
      headers,
    });
    
    const result = await response.json();
    if (!response.ok) {
      throw new Error(result.error || 'Failed to toggle landmine status');
    }
    
    // Handle new response format with success field
    if (result?.success && result?.landmine) {
      return result.landmine;
    }
    // Fallback for backward compatibility
    return result;
  } catch (error) {
    console.error('[Landmine API] Error toggling landmine status:', error);
    throw error;
  }
};

// Generate trigger variations using AI
export const generateLandmineVariations = async (trigger: string): Promise<string[]> => {
  try {
    const token = await getValidToken();
    const headers: Record<string, string> = {
      'Content-Type': 'application/json',
    };

    if (token) {
      headers['Authorization'] = `Bearer ${token}`;
    }

    const response = await fetch(`${apiConfig.baseURL}/api/landmines/generate-variations`, {
      method: 'POST',
      headers,
      body: JSON.stringify({ trigger }),
    });

    const result = await response.json();
    if (!response.ok) {
      throw new Error(result.error || 'Failed to generate variations');
    }

    if (result?.success && result?.variations) {
      return result.variations;
    }
    throw new Error('Failed to generate variations');
  } catch (error) {
    console.error('[Landmine API] Error generating variations:', error);
    throw error;
  }
};

// ============================================
// Competitor Management Functions
// ============================================

// Get all competitors
export const getCompetitors = async (page: number = 1, limit: number = 20): Promise<CompetitorsResponse> => {
  try {
    const response = await apiService.get<CompetitorsResponse>(`/competitors?page=${page}&limit=${limit}`);

    if (response?.success && response?.data) {
      return response.data;
    }

    // Fallback for different response structures
    if ((response as any)?.competitors) {
      return response as any;
    }

    return { competitors: [], pagination: null };
  } catch (error) {
    console.error('[Competitor API] Error fetching competitors:', error);
    throw error;
  }
};

// Get competitor by ID
export const getCompetitorById = async (id: string): Promise<CompetitorItem> => {
  try {
    const response = await apiService.get<CompetitorItem>(`/competitors/${id}`);

    if (response?.success && response?.data) {
      return response.data;
    }

    return response as any;
  } catch (error) {
    console.error('[Competitor API] Error fetching competitor:', error);
    throw error;
  }
};

// Create new competitor
export const addCompetitor = async (competitor: Partial<CompetitorItem>): Promise<CompetitorItem> => {
  try {
    const response = await apiService.post<CompetitorItem>('/competitors', competitor);

    if (response?.success && response?.data) {
      return response.data;
    }

    return response as any;
  } catch (error) {
    console.error('[Competitor API] Error creating competitor:', error);
    throw error;
  }
};

// Update competitor
export const updateCompetitor = async (id: string, updates: Partial<CompetitorItem>): Promise<CompetitorItem> => {
  try {
    const response = await apiService.put<CompetitorItem>(`/competitors/${id}`, updates);

    if (response?.success && response?.data) {
      return response.data;
    }

    return response as any;
  } catch (error) {
    console.error('[Competitor API] Error updating competitor:', error);
    throw error;
  }
};

// Delete competitor
export const deleteCompetitor = async (id: string): Promise<void> => {
  try {
    await apiService.delete(`/competitors/${id}`);
  } catch (error) {
    console.error('[Competitor API] Error deleting competitor:', error);
    throw error;
  }
};

// Add variation to competitor
// Add advantage to competitor
export const addCompetitorAdvantage = async (
  id: string,
  advantage: Partial<CompetitorAdvantage>
): Promise<CompetitorItem> => {
  try {
    const response = await apiService.post<CompetitorItem>(`/competitors/${id}/advantages`, advantage);

    if (response?.success && response?.data) {
      return response.data;
    }

    return response as any;
  } catch (error) {
    console.error('[Competitor API] Error adding advantage:', error);
    throw error;
  }
};

// Update advantage for competitor
export const updateCompetitorAdvantage = async (
  id: string,
  advantageId: string,
  advantage: Partial<CompetitorAdvantage>
): Promise<CompetitorItem> => {
  try {
    const response = await apiService.put<CompetitorItem>(`/competitors/${id}/advantages/${advantageId}`, advantage);

    if (response?.success && response?.data) {
      return response.data;
    }

    return response as any;
  } catch (error) {
    console.error('[Competitor API] Error updating advantage:', error);
    throw error;
  }
};

// Remove advantage from competitor
export const removeCompetitorAdvantage = async (id: string, advantageId: string): Promise<CompetitorItem> => {
  try {
    const response = await apiService.delete<CompetitorItem>(`/competitors/${id}/advantages/${advantageId}`);

    if (response?.success && response?.data) {
      return response.data;
    }

    return response as any;
  } catch (error) {
    console.error('[Competitor API] Error removing advantage:', error);
    throw error;
  }
};

// Add weakness to competitor
export const addCompetitorWeakness = async (
  id: string,
  weakness: Partial<CompetitorWeakness>
): Promise<CompetitorItem> => {
  try {
    const response = await apiService.post<CompetitorItem>(`/competitors/${id}/weaknesses`, weakness);

    if (response?.success && response?.data) {
      return response.data;
    }

    return response as any;
  } catch (error) {
    console.error('[Competitor API] Error adding weakness:', error);
    throw error;
  }
};

// Update weakness for competitor
export const updateCompetitorWeakness = async (
  id: string,
  weaknessId: string,
  weakness: Partial<CompetitorWeakness>
): Promise<CompetitorItem> => {
  try {
    const response = await apiService.put<CompetitorItem>(`/competitors/${id}/weaknesses/${weaknessId}`, weakness);

    if (response?.success && response?.data) {
      return response.data;
    }

    return response as any;
  } catch (error) {
    console.error('[Competitor API] Error updating weakness:', error);
    throw error;
  }
};

// Remove weakness from competitor
export const removeCompetitorWeakness = async (id: string, weaknessId: string): Promise<CompetitorItem> => {
  try {
    const response = await apiService.delete<CompetitorItem>(`/competitors/${id}/weaknesses/${weaknessId}`);

    if (response?.success && response?.data) {
      return response.data;
    }

    return response as any;
  } catch (error) {
    console.error('[Competitor API] Error removing weakness:', error);
    throw error;
  }
};

// Toggle competitor active status
export const toggleCompetitorStatus = async (id: string): Promise<CompetitorItem> => {
  try {
    const token = await getValidToken();
    const headers: Record<string, string> = {
      'Content-Type': 'application/json',
    };

    if (token) {
      headers['Authorization'] = `Bearer ${token}`;
    }

    const response = await fetch(`${apiConfig.baseURL}/api/competitors/${id}/toggle-status`, {
      method: 'PATCH',
      headers,
    });

    const result = await response.json();
    if (!response.ok) {
      throw new Error(result.error || 'Failed to toggle competitor status');
    }

    return result;
  } catch (error) {
    console.error('[Competitor API] Error toggling competitor status:', error);
    throw error;
  }
};

// Generate regex patterns for competitor name using AI
// ============================================
// Calendar Events API
// ============================================

export interface CalendarEvent {
  id: string;
  title: string;
  startTime: string;
  endTime: string;
  attendees: Array<{
    email: string;
    displayName: string;
    responseStatus: string;
  }>;
  meetingUrl: string | null;
  isBlacklisted: boolean;
  hasBotScheduled: boolean;
  botStatus: 'scheduled' | 'creating' | 'joining' | 'active' | null;
}

export interface CalendarEventsResponse {
  success: boolean;
  events: CalendarEvent[];
  range: string;
  blacklistedCount: number;
}

// Get calendar events with optional time range filter
export const getCalendarEvents = async (range: '1week' | '2weeks' | '1month' = '1week'): Promise<CalendarEventsResponse> => {
  try {
    const response = await apiService.get<CalendarEventsResponse>(`/calendar/events?range=${range}`);
    return response as any;
  } catch (error) {
    console.error('[Calendar API] Error fetching calendar events:', error);
    throw error;
  }
};

// Blacklist a calendar event (prevent bot from auto-joining)
export const blacklistCalendarEvent = async (eventId: string): Promise<ApiResponse> => {
  try {
    const response = await apiService.post(`/calendar/events/${eventId}/blacklist`);
    return response;
  } catch (error) {
    console.error('[Calendar API] Error blacklisting event:', error);
    throw error;
  }
};

// Remove event from blacklist (allow bot to auto-join)
export const unblacklistCalendarEvent = async (eventId: string): Promise<ApiResponse> => {
  try {
    const response = await apiService.delete(`/calendar/events/${eventId}/blacklist`);
    return response;
  } catch (error) {
    console.error('[Calendar API] Error removing event from blacklist:', error);
    throw error;
  }
};

// ==========================================
// Bulk Upload API
// ==========================================

export interface BulkUploadJobError {
  row: number;
  sheet: string;
  message: string;
  originalData?: {
    question?: string;
    answer?: string;
  };
}

export interface BulkUploadJob {
  _id: string;
  status: 'pending' | 'processing' | 'completed' | 'failed' | 'partial';
  fileName: string;
  uploadedBy?: string;
  draftTaskId: string;
  importMode: 'add_missing' | 'replace';
  totalObjections: number;
  totalFaqs: number;
  processedObjections: number;
  processedFaqs: number;
  failedObjections: number;
  failedFaqs: number;
  addedObjections?: number;
  addedFaqs?: number;
  skippedObjections?: number;
  skippedFaqs?: number;
  errors: BulkUploadJobError[];
  createdAt: string;
  updatedAt: string;
}

export interface BulkUploadJobsResponse {
  jobs: BulkUploadJob[];
  pagination: {
    page: number;
    limit: number;
    total: number;
    pages: number;
  };
}

// Upload Excel file for bulk processing
export const uploadBulkFile = async (
  file: File,
  options: { draftTaskId: string; importMode?: 'add_missing' | 'replace' }
): Promise<ApiResponse<{ job: BulkUploadJob }>> => {
  try {
    const formData = new FormData();
    formData.append('file', file);
    formData.append('draftTaskId', options.draftTaskId);
    formData.append('importMode', options.importMode || 'add_missing');

    const response = await apiService.postFormData<{ job: BulkUploadJob }>('/bulk-upload', formData);
    return response;
  } catch (error) {
    console.error('[BulkUpload API] Error uploading file:', error);
    throw error;
  }
};

// Get all bulk upload jobs (history)
export const getBulkUploadJobs = async (page: number = 1, limit: number = 20): Promise<BulkUploadJobsResponse> => {
  try {
    const response = await apiService.get<any>(`/bulk-upload/jobs?page=${page}&limit=${limit}`) as any;
    if (response?.success) {
      return {
        jobs: response.jobs || [],
        pagination: response.pagination || { page: 1, limit: 20, total: 0, pages: 1 }
      };
    }
    return { jobs: [], pagination: { page: 1, limit: 20, total: 0, pages: 1 } };
  } catch (error) {
    console.error('[BulkUpload API] Error getting jobs:', error);
    throw error;
  }
};

// Get single job details
export const getBulkUploadJob = async (jobId: string): Promise<BulkUploadJob> => {
  try {
    const response = await apiService.get<any>(`/bulk-upload/jobs/${jobId}`) as any;
    if (response?.success && response?.job) {
      return response.job;
    }
    throw new Error('Job not found');
  } catch (error) {
    console.error('[BulkUpload API] Error getting job:', error);
    throw error;
  }
};

// Download template file
export const downloadBulkUploadTemplate = async (): Promise<void> => {
  try {
    const token = await getValidToken();
    const headers: Record<string, string> = {};

    if (token) {
      headers['Authorization'] = `Bearer ${token}`;
    }

    const response = await fetch(`${apiConfig.baseURL}/api/bulk-upload/template`, {
      method: 'GET',
      headers,
    });

    if (!response.ok) {
      throw new Error('Failed to download template');
    }

    const blob = await response.blob();
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'bulk_upload_template.xlsx';
    document.body.appendChild(a);
    a.click();
    window.URL.revokeObjectURL(url);
    document.body.removeChild(a);
  } catch (error) {
    console.error('[BulkUpload API] Error downloading template:', error);
    throw error;
  }
};

// Download errors CSV for a job
export const downloadJobErrors = async (jobId: string): Promise<void> => {
  try {
    const token = await getValidToken();
    const headers: Record<string, string> = {};

    if (token) {
      headers['Authorization'] = `Bearer ${token}`;
    }

    const response = await fetch(`${apiConfig.baseURL}/api/bulk-upload/jobs/${jobId}/errors`, {
      method: 'GET',
      headers,
    });

    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.error || 'Failed to download errors');
    }

    const blob = await response.blob();
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `errors_${jobId}.csv`;
    document.body.appendChild(a);
    a.click();
    window.URL.revokeObjectURL(url);
    document.body.removeChild(a);
  } catch (error) {
    console.error('[BulkUpload API] Error downloading errors:', error);
    throw error;
  }
};

// ==========================================
// Knowledge Generator API
// ==========================================

export interface KnowledgeGeneratorVariation {
  tempId: string;
  question: string;
}

export interface KnowledgeGeneratorSuggestion {
  tempId: string;
  question: string;
  answer: string;
  category: string;
  sourceUrl?: string | null;
  /** Provenance of the generated content — informational only. The seeded
   *  vs. manual distinction that gates live-KB trust lives on the task-level
   *  superadmin initial-draft origin, not here. */
  source?: 'website' | 'ai_generated' | 'internet_seeded' | 'manual' | 'voice' | 'spreadsheet' | null;
  /** A bounded generation estimate used only for the first draft selection.
   * It is not a human approval or a factual-accuracy assertion. */
  confidence?: 'high' | 'needs_review' | 'unscored' | null;
  confidenceReason?: string;
  evidenceSources?: string[];
  styleCleanupStatus?: 'pending' | 'cleaned' | 'manual' | 'skipped' | 'error' | null;
  styleCleanedAt?: string | null;
  variations: KnowledgeGeneratorVariation[];
  approved: boolean;
  edited: boolean;
  synced: boolean;
  syncedAt?: string;
  removed?: boolean;
}

export interface KnowledgeGeneratorProgress {
  currentStep: 'researching' | 'generating' | 'syncing' | null;
  percentage: number;
  message: string;
}

export interface KnowledgeGeneratorResearch {
  summary: string;
  products: string[];
  keyFeatures: string[];
  targetAudience?: string;
  pricingModel?: string;
  howItWorks?: string;
  certifications?: string[];
  claimsReviewedAt?: string | null;
  claimsReviewedByUid?: string;
  claimsReviewedByEmail?: string;
  claimsReviewedVersion?: string;
  commonObjections: string[];
  faqTopics?: string[];
  pagesAnalyzed?: number;
  researchedAt: string;
  styleCleanupStatus?: 'pending' | 'cleaned' | 'manual' | 'skipped' | 'error' | null;
  styleCleanedAt?: string | null;
  styleCleanupPendingFields?: string[];
  styleCleanedFields?: string[];
  styleManuallyEditedFields?: string[];
  styleCleanupErrorFields?: string[];
}

export interface KnowledgeGeneratorError {
  message: string;
  step: string;
  timestamp: string;
  retryCount: number;
}

export interface KnowledgeGeneratorSyncResults {
  syncedAt: string;
  createdObjectionIds: string[];
  createdFaqIds: string[];
  createdLandmineIds?: string[];
  createdCompetitorIds?: string[];
  totalObjections: number;
  totalFaqs: number;
  totalLandmines?: number;
  totalCompetitors?: number;
  playbookPublished?: boolean;
  playbookName?: string | null;
  playbooksPublished?: number;
  playbookNames?: string[];
  taskCompleted?: boolean;
  remainingCount?: number;
  remainingApprovedCount?: number;
  /** Per-item failures from a partial publish. The items *not* listed here
   *  are live; a non-empty array means the publish was incomplete, not that
   *  it was rejected. */
  errors?: { type?: string; tempId?: string; error?: string }[];
  /** Non-blocking quality notice. These items were published with their
   * original manager-approved wording after background cleanup failed. */
  cleanupWarnings?: { type: string; tempId?: string; field?: string; message: string }[];
}

export interface KnowledgeGeneratorRepRuntimeReadiness {
  status: 'not_applicable' | 'indexing' | 'ready' | 'error';
  expected: number;
  ready: number;
  pending: number;
  failed: number;
  /** Per-dependency breakdown. Landmines/competitors/playbook are published
   * to Mongo but this backend has no channel to confirm they have reached
   * the websocket container's in-memory cache — see `unobservable`. */
  dependencies?: {
    qa: { status: 'not_applicable' | 'indexing' | 'ready' | 'error'; expected: number; ready: number; pending: number; failed: number };
    landmines: { status: 'not_applicable' | 'published_pending_cache' | 'error'; expected: number; published: number; cacheRefreshIntervalSeconds: number };
    competitors: { status: 'not_applicable' | 'published_pending_cache' | 'error'; expected: number; published: number; cacheRefreshIntervalSeconds: number };
    playbook: { status: 'not_applicable' | 'pending' | 'ready' | 'error' };
  };
  /** Signals this endpoint cannot currently observe (websocket process
   * health, Redis reload status, FAISS reload completion) — present so a UI
   * shows "unknown" rather than inferring "ready" from their absence. */
  unobservable?: string[];
}

export interface KnowledgeGeneratorLandmineDraft {
  tempId: string;
  owner?: string;
  trigger: string;
  triggerVariations?: string[];
  landmineType: 'legal_risk' | 'detractor';
  category?: string;
  priority: 'high' | 'medium' | 'low';
  canSay: Array<{ point: string; context?: string }>;
  cannotSay: Array<{ point: string; reason?: string; severity?: 'legal_risk' | 'compliance_risk' | 'brand_risk' | 'general' }>;
  quickTips?: string;
  approvalStatus?: 'approved' | 'pending';
  reviewedVersion?: string | null;
  reviewedAt?: string | null;
  reviewedByUid?: string | null;
  reviewedByEmail?: string | null;
  styleCleanupStatus?: 'pending' | 'cleaned' | 'manual' | 'skipped' | 'error' | null;
  styleCleanedAt?: string | null;
  approved: boolean;
  edited: boolean;
  synced: boolean;
  syncedAt?: string | null;
  removed?: boolean;
}

export const persistKnowledgeGeneratorDraftRemoval = async (
  taskId: string,
  type: 'objection' | 'websiteFaq' | 'aiGeneratedFaq' | 'landmine' | 'competitor',
  tempId: string,
  removed: boolean,
): Promise<KnowledgeGeneratorTask> => {
  try {
    const response = await apiService.patch<any>(
      `/knowledge-generator/tasks/${taskId}/draft-items/${type}/${tempId}/removal`,
      { removed },
    ) as any;
    if (response?.success && response?.data) return response.data;
    throw new Error(response?.error || 'Failed to save draft removal');
  } catch (error) {
    throw apiFailureAsError(error, 'Failed to save draft removal');
  }
};

export const mergeKnowledgeGeneratorObjectionVariation = async (
  taskId: string,
  sourceTempId: string,
  targetTempId: string,
): Promise<KnowledgeGeneratorTask> => {
  try {
    const response = await apiService.post<any>(
      `/knowledge-generator/tasks/${encodeURIComponent(taskId)}/suggestions/objections/${encodeURIComponent(sourceTempId)}/merge-variation`,
      { targetTempId },
    ) as any;
    if (response?.success && response?.data) return response.data;
    throw new Error(response?.error || 'Failed to add objection variation');
  } catch (error) {
    throw apiFailureAsError(error, 'Failed to add objection variation');
  }
};

export const persistKnowledgeGeneratorOnboardingSelection = async (
  taskId: string,
  type: 'landmine' | 'competitor',
  tempId: string,
  approved: boolean
): Promise<KnowledgeGeneratorTask> => {
  try {
    const response = await apiService.patch<any>(
      `/knowledge-generator/tasks/${taskId}/onboarding-draft/${type}/${tempId}/selection`,
      { approved }
    ) as any;
    if (response?.success && response?.data) return response.data;
    throw new Error(response?.error || 'Failed to save selection');
  } catch (error) {
    throw apiFailureAsError(error, 'Failed to save selection');
  }
};

export const reviewKnowledgeGeneratorLandmine = async (
  taskId: string,
  selectedTempIds: string[],
  reviewedVersion: string,
  reviewed = true
): Promise<KnowledgeGeneratorTask> => {
  try {
    const response = await apiService.post<any>(
      `/knowledge-generator/tasks/${taskId}/onboarding-draft/landmines/review`,
      { selectedTempIds, reviewedVersion, reviewed }
    ) as any;
    if (response?.success && response?.data) return response.data;
    throw new Error(response?.error || 'Failed to record landmine review');
  } catch (error) {
    throw apiFailureAsError(error, 'Failed to record landmine review');
  }
};

export const updateKnowledgeGeneratorLandmineDraft = async (
  taskId: string,
  tempId: string,
  draft: Partial<KnowledgeGeneratorLandmineDraft>,
  expectedVersion: string
): Promise<KnowledgeGeneratorTask> => {
  try {
    const response = await apiService.patch<any>(
      `/knowledge-generator/tasks/${taskId}/onboarding-draft/landmines/${tempId}`,
      { ...draft, expectedVersion }
    ) as any;
    if (response?.success && response?.data) return response.data;
    throw new Error(response?.error || 'Failed to save landmine');
  } catch (error) {
    throw apiFailureAsError(error, 'Failed to save landmine');
  }
};

export const retryKnowledgeGeneratorStarterPlaybook = async (taskId: string): Promise<KnowledgeGeneratorTask> => {
  const response = await apiService.post<any>(
    `/knowledge-generator/tasks/${taskId}/onboarding-draft/starter-playbook/retry`,
    {}
  ) as any;
  if (response?.success && response?.data) return response.data;
  throw new Error(response?.error || 'Failed to retry starter playbook generation');
};

export const persistKnowledgeGeneratorStarterPlaybookSelection = async (
  taskId: string,
  included: boolean
): Promise<KnowledgeGeneratorTask> => {
  const response = await apiService.patch<any>(
    `/knowledge-generator/tasks/${taskId}/onboarding-draft/starter-playbook/selection`,
    { included }
  ) as any;
  if (response?.success && response?.data) return response.data;
  throw new Error(response?.error || 'Failed to save starter playbook selection');
};

export interface KnowledgeGeneratorCompetitorDraft {
  tempId: string;
  owner?: string;
  name: string;
  advantages: CompetitorAdvantage[];
  weaknesses: CompetitorWeakness[];
  talking_points?: string;
  category: 'direct_competitor' | 'indirect_competitor' | 'alternative_solution';
  priority: 'high' | 'medium' | 'low';
  approvalStatus?: 'approved' | 'pending';
  reviewedVersion?: string | null;
  reviewedAt?: string | null;
  reviewedByUid?: string | null;
  reviewedByEmail?: string | null;
  styleCleanupStatus?: 'pending' | 'cleaned' | 'manual' | 'skipped' | 'error' | null;
  styleCleanedAt?: string | null;
  approved: boolean;
  edited: boolean;
  synced: boolean;
  syncedAt?: string | null;
  removed?: boolean;
}

export const reviewKnowledgeGeneratorCompetitor = async (
  taskId: string,
  selectedTempIds: string[],
  reviewedVersion: string,
  reviewed = true
): Promise<KnowledgeGeneratorTask> => {
  try {
    const response = await apiService.post<any>(
      `/knowledge-generator/tasks/${taskId}/onboarding-draft/competitors/review`,
      { selectedTempIds, reviewedVersion, reviewed }
    ) as any;
    if (response?.success && response?.data) return response.data;
    throw new Error(response?.error || 'Failed to record competitor review');
  } catch (error) {
    throw apiFailureAsError(error, 'Failed to record competitor review');
  }
};

export const updateKnowledgeGeneratorCompetitorDraft = async (
  taskId: string,
  tempId: string,
  draft: Partial<KnowledgeGeneratorCompetitorDraft>,
  expectedVersion: string
): Promise<KnowledgeGeneratorTask> => {
  try {
    const response = await apiService.patch<any>(
      `/knowledge-generator/tasks/${taskId}/onboarding-draft/competitors/${tempId}`,
      { ...draft, expectedVersion }
    ) as any;
    if (response?.success && response?.data) return response.data;
    throw new Error(response?.error || 'Failed to save competitor');
  } catch (error) {
    throw apiFailureAsError(error, 'Failed to save competitor');
  }
};

export interface KnowledgeGeneratorTask {
  _id: string;
  companyName: string;
  websiteUrl: string;
  companyDescription: string;
  crawledPagesCount?: number;
  createdBy: string;
  createdByEmail?: string;
  status: 'pending' | 'researching' | 'generating' | 'ready_for_review' | 'syncing' | 'completed' | 'error';
  generationMode?: 'initial' | 'rescan';
  progress: KnowledgeGeneratorProgress;
  researchContent?: KnowledgeGeneratorResearch;
  suggestions?: {
    objections: KnowledgeGeneratorSuggestion[];
    websiteFaqs: KnowledgeGeneratorSuggestion[];
    aiGeneratedFaqs: KnowledgeGeneratorSuggestion[];
  };
  onboardingDraft?: {
    landmines: KnowledgeGeneratorLandmineDraft[];
    competitors: KnowledgeGeneratorCompetitorDraft[];
    starterPlaybookTaskId?: string | null;
    starterPlaybookIncluded?: boolean;
    starterPlaybookError?: string | null;
    landmineSetReview?: {
      version?: string | null;
      reviewedTempIds?: string[];
      reviewedAt?: string | null;
      reviewedByUid?: string | null;
      reviewedByEmail?: string | null;
    };
    competitorSetReview?: {
      version?: string | null;
      reviewedTempIds?: string[];
      reviewedAt?: string | null;
      reviewedByUid?: string | null;
      reviewedByEmail?: string | null;
    };
  };
  syncResults?: KnowledgeGeneratorSyncResults;
  error?: KnowledgeGeneratorError;
  // Separate sync status (doesn't affect main task status)
  syncStatus?: 'idle' | 'syncing' | 'success' | 'error' | null;
  syncError?: KnowledgeGeneratorError;
  createdAt: string;
  updatedAt: string;
  completedAt?: string;
  // Counts returned from list API (aggregation)
  suggestedObjectionsCount?: number;
  suggestedWebsiteFaqsCount?: number;
  suggestedAiGeneratedFaqsCount?: number;
  suggestedFaqsCount?: number; // Combined count for backwards compatibility
  suggestedLandminesCount?: number;
  suggestedCompetitorsCount?: number;
  // Unsynced counts from the list endpoint. These keep deliberately
  // unchecked drafts discoverable even on tasks completed by older code.
  unpublishedObjectionsCount?: number;
  unpublishedWebsiteFaqsCount?: number;
  unpublishedAiGeneratedFaqsCount?: number;
  unpublishedFaqsCount?: number;
  unpublishedLandminesCount?: number;
  unpublishedCompetitorsCount?: number;
  unpublishedDraftCount?: number;
  selectedDraftAnswersCount?: number;
  selectedDraftLandminesCount?: number;
  selectedDraftCompetitorsCount?: number;
}

export interface KnowledgeGeneratorTasksResponse {
  data: KnowledgeGeneratorTask[];
  pagination: {
    page: number;
    limit: number;
    total: number;
    pages: number;
  };
}

export interface DuplicateMatch {
  _id: string;
  question: string;
  answer: string;
  category: string;
  similarity: number;
  confidenceLevel: 'high' | 'medium' | 'low';
}

export interface DuplicateCheckResult {
  suggestion: KnowledgeGeneratorSuggestion;
  matches: DuplicateMatch[];
  type: 'objection' | 'websiteFaq' | 'aiGeneratedFaq';
}

export interface DuplicateCheckResponse {
  duplicates: {
    objections: DuplicateCheckResult[];
    websiteFaqs: DuplicateCheckResult[];
    aiGeneratedFaqs: DuplicateCheckResult[];
  };
  grouped: {
    high: DuplicateCheckResult[];
    medium: DuplicateCheckResult[];
    low: DuplicateCheckResult[];
  };
  noDuplicates: {
    objections: KnowledgeGeneratorSuggestion[];
    websiteFaqs: KnowledgeGeneratorSuggestion[];
    aiGeneratedFaqs: KnowledgeGeneratorSuggestion[];
  };
  summary: {
    totalApproved: number;
    withDuplicates: number;
    noDuplicates: number;
    highConfidence: number;
    mediumConfidence: number;
    lowConfidence: number;
  };
}


// Create a new knowledge generator task
export const createKnowledgeGeneratorTask = async (
  companyName: string,
  websiteUrl: string,
  companyDescription: string
): Promise<KnowledgeGeneratorTask> => {
  try {
    const response = await apiService.post<any>('/knowledge-generator/tasks', {
      companyName,
      websiteUrl,
      companyDescription
    });
    if (response?.success && response?.data) {
      return response.data;
    }
    throw new Error(response?.error || 'Failed to create task');
  } catch (error) {
    console.error('[KnowledgeGenerator API] Error creating task:', error);
    throw error;
  }
};

// Explicitly resolve the tenant's writable knowledge draft before opening a
// writer. The returned id becomes the authority for every subsequent guide
// or spreadsheet request; the write endpoint never has to guess a target.
export const openKnowledgeGeneratorDraft = async (): Promise<KnowledgeGeneratorTask> => {
  try {
    const response = await apiService.post<any>('/knowledge-generator/tasks/open-draft', {}) as any;
    if (response?.success && response?.data) return response.data;
    throw new Error(response?.error || 'Failed to open knowledge draft');
  } catch (error) {
    console.error('[KnowledgeGenerator API] Error opening draft:', error);
    throw error;
  }
};

export const rescanKnowledgeGeneratorTask = async (
  taskId: string
): Promise<KnowledgeGeneratorTask> => {
  try {
    const response = await apiService.post<any>(`/knowledge-generator/tasks/${taskId}/rescan`, {}) as any;
    if (response?.success && response?.data) return response.data;
    throw new Error(response?.error || 'Failed to start website scan');
  } catch (error) {
    console.error('[KnowledgeGenerator API] Error re-scanning website:', error);
    throw error;
  }
};

// Get all knowledge generator tasks
export const getKnowledgeGeneratorTasks = async (
  page: number = 1,
  limit: number = 20,
  status?: string
): Promise<KnowledgeGeneratorTasksResponse> => {
  try {
    const params: Record<string, string> = {
      page: page.toString(),
      limit: limit.toString()
    };
    if (status) {
      params.status = status;
    }
    const response = await apiService.get<any>('/knowledge-generator/tasks', params) as any;
    if (response?.success) {
      return {
        data: response.data || [],
        pagination: response.pagination || { page: 1, limit: 20, total: 0, pages: 1 }
      };
    }
    return { data: [], pagination: { page: 1, limit: 20, total: 0, pages: 1 } };
  } catch (error) {
    console.error('[KnowledgeGenerator API] Error getting tasks:', error);
    throw error;
  }
};

// Get single knowledge generator task
export const getKnowledgeGeneratorTask = async (taskId: string): Promise<KnowledgeGeneratorTask> => {
  try {
    const response = await apiService.get<any>(`/knowledge-generator/tasks/${taskId}`) as any;
    if (response?.success && response?.data) {
      return response.data;
    }
    throw new Error('Task not found');
  } catch (error) {
    console.error('[KnowledgeGenerator API] Error getting task:', error);
    throw error;
  }
};

// Update suggestions (bulk update)
export const updateKnowledgeGeneratorSuggestions = async (
  taskId: string,
  suggestions: {
    objections?: KnowledgeGeneratorSuggestion[];
    websiteFaqs?: KnowledgeGeneratorSuggestion[];
    aiGeneratedFaqs?: KnowledgeGeneratorSuggestion[];
  }
): Promise<KnowledgeGeneratorTask> => {
  try {
    const response = await apiService.put<any>(`/knowledge-generator/tasks/${taskId}/suggestions`, {
      suggestions
    }) as any;
    if (response?.success && response?.data) {
      return response.data;
    }
    throw new Error(response?.error || 'Failed to update suggestions');
  } catch (error) {
    console.error('[KnowledgeGenerator API] Error updating suggestions:', error);
    throw error;
  }
};


// Update a single suggestion
export const updateKnowledgeGeneratorSuggestion = async (
  taskId: string,
  tempId: string,
  type: 'objection' | 'websiteFaq' | 'aiGeneratedFaq',
  updates: Partial<KnowledgeGeneratorSuggestion>
): Promise<KnowledgeGeneratorTask> => {
  try {
    const response = await apiService.put<any>(`/knowledge-generator/tasks/${taskId}/suggestions/${tempId}`, {
      type,
      updates
    }) as any;
    if (!response?.success || !response?.data) {
      throw new Error(response?.error || 'Failed to update suggestion');
    }
    return response.data;
  } catch (error) {
    console.error('[KnowledgeGenerator API] Error updating suggestion:', error);
    throw error;
  }
};

export const generateKnowledgeGeneratorSuggestionVariations = async (
  taskId: string,
  tempId: string,
  type: 'websiteFaq' | 'aiGeneratedFaq',
  question: string,
  answer: string,
  count: number = 3,
): Promise<string[]> => {
  try {
    const response = await apiService.post<any>(
      `/knowledge-generator/tasks/${taskId}/suggestions/${tempId}/generate-variations`,
      { type, question, answer, count },
    ) as any;
    if (response?.success && Array.isArray(response.variations)) return response.variations;
    throw new Error(response?.error || 'Failed to generate FAQ variations');
  } catch (error) {
    throw apiFailureAsError(error, 'Failed to generate FAQ variations');
  }
};

export const updateKnowledgeGeneratorCompanyProfile = async (
  taskId: string,
  field: 'whatYouSell' | 'targetAudience' | 'howItWorks' | 'certifications' | 'differentiators',
  value: string | string[]
): Promise<KnowledgeGeneratorTask> => {
  try {
    const response = await apiService.put<any>(`/knowledge-generator/tasks/${taskId}/company-profile`, {
      field,
      value
    }) as any;
    if (response?.success && response?.data) return response.data;
    throw new Error(response?.error || 'Failed to save company profile draft');
  } catch (error) {
    throw apiFailureAsError(error, 'Failed to save company profile draft');
  }
};

export const markKnowledgeGeneratorClaimsReviewed = async (
  taskId: string,
  certifications: string[],
): Promise<KnowledgeGeneratorTask> => {
  const response = await apiService.post<any>(`/knowledge-generator/tasks/${taskId}/company-profile/claims-reviewed`, { certifications });
  if (response?.success && response?.data) return response.data;
  throw new Error(response?.error || 'Failed to mark company claims as reviewed');
};

// Check for duplicates before syncing
export const checkKnowledgeGeneratorDuplicates = async (
  taskId: string,
  threshold: number = 0.5
): Promise<DuplicateCheckResponse> => {
  try {
    const response = await apiService.post<any>(`/knowledge-generator/tasks/${taskId}/check-duplicates`, {
      threshold
    }) as any;
    if (response?.success && response?.data) {
      return response.data;
    }
    throw new Error(response?.error || 'Failed to check duplicates');
  } catch (error) {
    console.error('[KnowledgeGenerator API] Error checking duplicates:', error);
    throw error;
  }
};

export interface DraftDuplicatePair {
  a: { tempId: string; question: string; kind: 'objection' | 'websiteFaq' | 'aiGeneratedFaq' };
  b: { tempId: string; question: string; kind: 'objection' | 'websiteFaq' | 'aiGeneratedFaq' };
  similarity: number;
  confidenceLevel: 'high' | 'medium' | 'low' | 'none';
  version: string;
  keptBoth: boolean;
}

// Two unpublished draft items that duplicate each other.
export const getKnowledgeGeneratorDraftDuplicates = async (
  taskId: string
): Promise<DraftDuplicatePair[]> => {
  try {
    const response = await apiService.get<any>(`/knowledge-generator/tasks/${taskId}/draft-duplicates`) as any;
    if (response?.success && response?.data) {
      return response.data.pairs || [];
    }
    throw new Error(response?.error || 'Failed to check draft duplicates');
  } catch (error) {
    console.error('[KnowledgeGenerator API] Error checking draft duplicates:', error);
    throw error;
  }
};

export const keepBothKnowledgeGeneratorDraftDuplicates = async (
  taskId: string,
  pairVersion: string,
): Promise<DraftDuplicatePair> => {
  try {
    const response = await apiService.post<any>(
      `/knowledge-generator/tasks/${taskId}/draft-duplicates/keep-both`,
      { pairVersion },
    ) as any;
    if (response?.success && response?.data) return response.data;
    throw new Error(response?.error || 'Failed to save duplicate review');
  } catch (error) {
    throw apiFailureAsError(error, 'Failed to save duplicate review');
  }
};

export const publishKnowledgeGeneratorTask = async (
  taskId: string,
  payload: {
    suggestions?: {
      objections?: KnowledgeGeneratorSuggestion[];
      websiteFaqs?: KnowledgeGeneratorSuggestion[];
      aiGeneratedFaqs?: KnowledgeGeneratorSuggestion[];
    };
    companyProfile?: {
      include: boolean;
      whatYouSell: string;
      targetAudience: string;
      howItWorks: string;
      certifications: string[];
      differentiators: string[];
      editedFields?: string[];
    };
    objectionTempIds?: string[];
    websiteFaqTempIds?: string[];
    aiGeneratedFaqTempIds?: string[];
    landmineTempIds?: string[];
    competitorTempIds?: string[];
    includeStarterPlaybook?: boolean;
    removedSuggestionTempIds?: string[];
  }
): Promise<KnowledgeGeneratorSyncResults> => {
  try {
    const response = await apiService.post<any>(`/knowledge-generator/tasks/${taskId}/publish-all`, payload) as any;
    // A publish is resumable, not atomic: the endpoint reports `success:
    // false` when *any* item failed, but the ones that succeeded are already
    // live and recorded on the task. Throwing away `data` in that case left
    // the caller unable to tell "nothing published" from "ten of twelve
    // published", so partial results come back as results — `errors` on them
    // is what says the publish was incomplete.
    if (response?.data) {
      return response.data;
    }
    throw new Error(response?.error || 'Failed to publish task');
  } catch (error) {
    console.error('[KnowledgeGenerator API] Error publishing task:', error);
    throw error;
  }
};

export const getKnowledgeGeneratorRepRuntimeReadiness = async (
  taskId: string,
): Promise<KnowledgeGeneratorRepRuntimeReadiness> => {
  const response = await apiService.get<any>(`/knowledge-generator/tasks/${taskId}/rep-runtime-readiness`) as any;
  if (response?.success && response?.data) return response.data;
  throw new Error(response?.error || 'Could not check live guidance readiness.');
};

// Retry failed task
export const retryKnowledgeGeneratorTask = async (taskId: string): Promise<KnowledgeGeneratorTask> => {
  try {
    const response = await apiService.post<any>(`/knowledge-generator/tasks/${taskId}/retry`) as any;
    if (response?.success && response?.data) {
      return response.data;
    }
    throw new Error(response?.error || 'Failed to retry task');
  } catch (error) {
    console.error('[KnowledgeGenerator API] Error retrying task:', error);
    throw error;
  }
};

// Delete task
export const deleteKnowledgeGeneratorTask = async (taskId: string): Promise<void> => {
  try {
    const response = await apiService.delete<any>(`/knowledge-generator/tasks/${taskId}`) as any;
    if (!response?.success) {
      throw new Error(response?.error || 'Failed to delete task');
    }
  } catch (error) {
    console.error('[KnowledgeGenerator API] Error deleting task:', error);
    throw error;
  }
};

// Get available categories
export const getKnowledgeGeneratorCategories = async (): Promise<string[]> => {
  try {
    const response = await apiService.get<any>('/knowledge-generator/categories') as any;
    if (response?.success && response?.data) {
      return response.data;
    }
    return [];
  } catch (error) {
    console.error('[KnowledgeGenerator API] Error getting categories:', error);
    throw error;
  }
};


// ============================================
// Sales Playbook Management Functions
// ============================================

// Playbook model types live in src/types/playbook.ts; re-exported here so
// existing `from '../services/apiService'` imports keep working.
export type {
  SalesPlaybook,
  SalesPlaybookOption,
  SalesPlaybookPitchBullet,
  SalesPlaybookQuestion,
  SalesPlaybookRole,
  SalesPlaybookTag,
} from '../types/playbook';
export { normalizePlaybook, normalizePlaybookOption } from '../types/playbook';

export interface SalesPlaybooksResponse {
  salesPlaybooks: SalesPlaybook[];
  pagination: PaginationInfo | null;
}

export interface SalesPlaybookTagsResponse {
  tags: SalesPlaybookTag[];
  labels: Record<SalesPlaybookTag, string>;
}

// Playbook Library types
export type PlaybookLibraryType = 'pitch' | 'question' | 'advanced';

export interface PlaybookLibraryItem {
  _id: string;
  type: PlaybookLibraryType;
  text: string;
  sortOrder: number;
  isActive: boolean;
  createdBy: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface PlaybookLibraryResponse {
  items: PlaybookLibraryItem[];
  pagination: PaginationInfo | null;
}

// Backward compatibility type aliases
export type TalkTrackTag = SalesPlaybookTag;
export type TalkTrackPitchBullet = SalesPlaybookPitchBullet;
export type TalkTrackQuestion = SalesPlaybookQuestion;
export type TalkTrackRole = SalesPlaybookRole;
export type TalkTrackPlaybook = SalesPlaybook;

// Shared envelope unwrap + normalization for every endpoint returning a
// single playbook document.
const unwrapPlaybookResponse = (response: ApiResponse<SalesPlaybook> | SalesPlaybook | null | undefined): SalesPlaybook => {
  const envelope = response as ApiResponse<SalesPlaybook> | null | undefined;
  return normalizePlaybook(envelope?.success && envelope?.data ? envelope.data : response);
};

// Get available tags
export const getSalesPlaybookTags = async (): Promise<SalesPlaybookTagsResponse> => {
  try {
    const response = await apiService.get<SalesPlaybookTagsResponse>('/salesplaybooks/tags');
    if (response?.success && response?.data) {
      return response.data;
    }
    return response as any;
  } catch (error) {
    console.error('[SalesPlaybook API] Error fetching tags:', error);
    throw error;
  }
};

// Get active playbooks for dropdown.
// NOTE: '/salesplaybooks/industries' is the backend's legacy URL alias of
// GET /salesplaybooks/playbooks; switching to the new path is a follow-up
// once every client is off the alias.
export const getSalesPlaybookOptions = async (): Promise<SalesPlaybookOption[]> => {
  try {
    const response = await apiService.get<any>('/salesplaybooks/industries');
    if (response?.success && response?.data) {
      return (Array.isArray(response.data) ? response.data : []).map(normalizePlaybookOption);
    }
    if (Array.isArray(response)) {
      return response.map(normalizePlaybookOption);
    }
    return [];
  } catch (error) {
    console.error('[SalesPlaybook API] Error fetching playbook options:', error);
    throw error;
  }
};

// Get all sales playbooks (admin)
export const getSalesPlaybooks = async (page: number = 1, limit: number = 50): Promise<SalesPlaybooksResponse> => {
  try {
    const response = await apiService.get<SalesPlaybooksResponse>(`/salesplaybooks?page=${page}&limit=${limit}`);
    const rawList: SalesPlaybooksResponse | null =
      response?.success && response?.data
        ? response.data
        : (response as any)?.salesPlaybooks
          ? (response as any)
          : null;
    if (rawList) {
      return {
        salesPlaybooks: (Array.isArray(rawList.salesPlaybooks) ? rawList.salesPlaybooks : []).map(normalizePlaybook),
        pagination: rawList.pagination ?? null,
      };
    }
    return { salesPlaybooks: [], pagination: null };
  } catch (error) {
    console.error('[SalesPlaybook API] Error fetching sales playbook:', error);
    throw error;
  }
};

// Get sales playbook by name
export const getSalesPlaybookByName = async (playbookName: string): Promise<SalesPlaybook | null> => {
  try {
    const response = await apiService.get<SalesPlaybook>(`/salesplaybooks/${playbookName}`);
    const rawPlaybook = response?.success && response?.data ? response.data : response;
    return rawPlaybook ? normalizePlaybook(rawPlaybook) : null;
  } catch (error) {
    console.error('[SalesPlaybook API] Error fetching sales playbook:', error);
    throw error;
  }
};

// Create new playbook (the backend derives playbookName from displayName)
export const createSalesPlaybook = async (displayName: string): Promise<SalesPlaybook> => {
  try {
    const response = await apiService.post<SalesPlaybook>('/salesplaybooks', { displayName });
    return unwrapPlaybookResponse(response);
  } catch (error) {
    console.error('[SalesPlaybook API] Error creating sales playbook:', error);
    throw error;
  }
};

// Update playbook
export const updateSalesPlaybook = async (playbookName: string, updates: Partial<SalesPlaybook>): Promise<SalesPlaybook> => {
  try {
    const response = await apiService.put<SalesPlaybook>(`/salesplaybooks/${playbookName}`, updates);
    return unwrapPlaybookResponse(response);
  } catch (error) {
    console.error('[SalesPlaybook API] Error updating sales playbook:', error);
    throw error;
  }
};

// Delete playbook
export const deleteSalesPlaybook = async (playbookName: string): Promise<void> => {
  try {
    await apiService.delete(`/salesplaybooks/${playbookName}`);
  } catch (error) {
    console.error('[SalesPlaybook API] Error deleting sales playbook:', error);
    throw error;
  }
};

// Toggle sales playbook status
export const toggleSalesPlaybookStatus = async (playbookName: string): Promise<SalesPlaybook> => {
  try {
    const response = await apiService.patch<SalesPlaybook>(`/salesplaybooks/${playbookName}/toggle-status`);
    return unwrapPlaybookResponse(response);
  } catch (error) {
    console.error('[SalesPlaybook API] Error toggling sales playbook status:', error);
    throw error;
  }
};

// Add role to playbook
export const addSalesPlaybookRole = async (playbookName: string, displayName: string): Promise<SalesPlaybook> => {
  try {
    const response = await apiService.post<SalesPlaybook>(`/salesplaybooks/${playbookName}/roles`, { displayName });
    return unwrapPlaybookResponse(response);
  } catch (error) {
    console.error('[SalesPlaybook API] Error adding role:', error);
    throw error;
  }
};

// Update role
export const updateSalesPlaybookRole = async (playbookName: string, roleId: string, updates: Partial<SalesPlaybookRole>): Promise<SalesPlaybook> => {
  try {
    const response = await apiService.put<SalesPlaybook>(`/salesplaybooks/${playbookName}/roles/${roleId}`, updates);
    return unwrapPlaybookResponse(response);
  } catch (error) {
    console.error('[SalesPlaybook API] Error updating role:', error);
    throw error;
  }
};

// Delete role
export const deleteSalesPlaybookRole = async (playbookName: string, roleId: string): Promise<SalesPlaybook> => {
  try {
    const response = await apiService.delete<SalesPlaybook>(`/salesplaybooks/${playbookName}/roles/${roleId}`);
    return unwrapPlaybookResponse(response);
  } catch (error) {
    console.error('[SalesPlaybook API] Error deleting role:', error);
    throw error;
  }
};

// Add pitch bullet to role
export const addSalesPlaybookPitchBullet = async (playbookName: string, roleId: string, text: string): Promise<SalesPlaybook> => {
  try {
    const response = await apiService.post<SalesPlaybook>(`/salesplaybooks/${playbookName}/roles/${roleId}/pitch`, { text });
    return unwrapPlaybookResponse(response);
  } catch (error) {
    console.error('[SalesPlaybook API] Error adding pitch bullet:', error);
    throw error;
  }
};

// Update pitch bullet
export const updateSalesPlaybookPitchBullet = async (playbookName: string, roleId: string, pitchId: string, text: string): Promise<SalesPlaybook> => {
  try {
    const response = await apiService.put<SalesPlaybook>(`/salesplaybooks/${playbookName}/roles/${roleId}/pitch/${pitchId}`, { text });
    return unwrapPlaybookResponse(response);
  } catch (error) {
    console.error('[SalesPlaybook API] Error updating pitch bullet:', error);
    throw error;
  }
};

// Delete pitch bullet
export const deleteSalesPlaybookPitchBullet = async (playbookName: string, roleId: string, pitchId: string): Promise<SalesPlaybook> => {
  try {
    const response = await apiService.delete<SalesPlaybook>(`/salesplaybooks/${playbookName}/roles/${roleId}/pitch/${pitchId}`);
    return unwrapPlaybookResponse(response);
  } catch (error) {
    console.error('[SalesPlaybook API] Error deleting pitch bullet:', error);
    throw error;
  }
};

// Add advanced bullet to role
export const addSalesPlaybookAdvancedBullet = async (playbookName: string, roleId: string, text: string): Promise<SalesPlaybook> => {
  try {
    const response = await apiService.post<SalesPlaybook>(`/salesplaybooks/${playbookName}/roles/${roleId}/advanced`, { text });
    return unwrapPlaybookResponse(response);
  } catch (error) {
    console.error('[SalesPlaybook API] Error adding advanced bullet:', error);
    throw error;
  }
};

// Update advanced bullet
export const updateSalesPlaybookAdvancedBullet = async (playbookName: string, roleId: string, advancedId: string, text: string): Promise<SalesPlaybook> => {
  try {
    const response = await apiService.put<SalesPlaybook>(`/salesplaybooks/${playbookName}/roles/${roleId}/advanced/${advancedId}`, { text });
    return unwrapPlaybookResponse(response);
  } catch (error) {
    console.error('[SalesPlaybook API] Error updating advanced bullet:', error);
    throw error;
  }
};

// Delete advanced bullet
export const deleteSalesPlaybookAdvancedBullet = async (playbookName: string, roleId: string, advancedId: string): Promise<SalesPlaybook> => {
  try {
    const response = await apiService.delete<SalesPlaybook>(`/salesplaybooks/${playbookName}/roles/${roleId}/advanced/${advancedId}`);
    return unwrapPlaybookResponse(response);
  } catch (error) {
    console.error('[SalesPlaybook API] Error deleting advanced bullet:', error);
    throw error;
  }
};

// Add question to role
export const addSalesPlaybookQuestion = async (playbookName: string, roleId: string, questionText: string, tags: SalesPlaybookTag[]): Promise<SalesPlaybook> => {
  try {
    const response = await apiService.post<SalesPlaybook>(`/salesplaybooks/${playbookName}/roles/${roleId}/questions`, { questionText, tags });
    return unwrapPlaybookResponse(response);
  } catch (error) {
    console.error('[SalesPlaybook API] Error adding question:', error);
    throw error;
  }
};

// Update question
export const updateSalesPlaybookQuestion = async (playbookName: string, roleId: string, questionId: string, updates: Partial<SalesPlaybookQuestion>): Promise<SalesPlaybook> => {
  try {
    const response = await apiService.put<SalesPlaybook>(`/salesplaybooks/${playbookName}/roles/${roleId}/questions/${questionId}`, updates);
    return unwrapPlaybookResponse(response);
  } catch (error) {
    console.error('[SalesPlaybook API] Error updating question:', error);
    throw error;
  }
};

// Delete question
export const deleteSalesPlaybookQuestion = async (playbookName: string, roleId: string, questionId: string): Promise<SalesPlaybook> => {
  try {
    const response = await apiService.delete<SalesPlaybook>(`/salesplaybooks/${playbookName}/roles/${roleId}/questions/${questionId}`);
    return unwrapPlaybookResponse(response);
  } catch (error) {
    console.error('[SalesPlaybook API] Error deleting question:', error);
    throw error;
  }
};

// Bulk update all roles for a playbook (single API call)
export const bulkUpdateSalesPlaybookRoles = async (
  playbookName: string,
  roles: {
    originalId?: string;
    displayName: string;
    pitchBullets: Array<string | { _id: string; text: string }>;
    advancedBullets: Array<string | { _id: string; text: string }>;
    questions: { _id?: string; text: string; tags: SalesPlaybookTag[] }[];
  }[]
): Promise<SalesPlaybook> => {
  try {
    const response = await apiService.put<SalesPlaybook>(
      `/salesplaybooks/${playbookName}/bulk-update-roles`,
      { roles }
    );
    return unwrapPlaybookResponse(response);
  } catch (error) {
    console.error('[SalesPlaybook API] Error bulk updating roles:', error);
    throw error;
  }
};

// ========== Playbook Dictionary API ==========

export const getPlaybookLibraryItems = async (
  type?: PlaybookLibraryType,
  page: number = 1,
  limit: number = 500,
  search?: string
): Promise<PlaybookLibraryResponse> => {
  try {
    let endpoint = `/salesplaybook-library?page=${page}&limit=${limit}`;
    if (type) endpoint += `&type=${type}`;
    if (search) endpoint += `&search=${encodeURIComponent(search)}`;

    const response = await apiService.get<PlaybookLibraryResponse>(endpoint);
    if (response?.success && (response as any)?.items) {
      return { items: (response as any).items, pagination: (response as any).pagination };
    }
    if (response?.success && response?.data) {
      return response.data;
    }
    return { items: [], pagination: null };
  } catch (error) {
    console.error('[PlaybookLibrary API] Error fetching items:', error);
    throw error;
  }
};

export const createPlaybookLibraryItem = async (
  type: PlaybookLibraryType,
  text: string
): Promise<PlaybookLibraryItem> => {
  try {
    const response = await apiService.post<{ item: PlaybookLibraryItem }>(
      '/salesplaybook-library',
      { type, text }
    );
    if (response?.success && (response as any)?.item) {
      return (response as any).item;
    }
    if (response?.success && response?.data) {
      return (response.data as any).item || response.data;
    }
    return response as any;
  } catch (error) {
    console.error('[PlaybookLibrary API] Error creating item:', error);
    throw error;
  }
};

export const updatePlaybookLibraryItem = async (
  id: string,
  text: string
): Promise<PlaybookLibraryItem> => {
  try {
    const response = await apiService.put<{ item: PlaybookLibraryItem }>(
      `/salesplaybook-library/${id}`,
      { text }
    );
    if (response?.success && (response as any)?.item) {
      return (response as any).item;
    }
    if (response?.success && response?.data) {
      return (response.data as any).item || response.data;
    }
    return response as any;
  } catch (error) {
    console.error('[PlaybookLibrary API] Error updating item:', error);
    throw error;
  }
};

export const deletePlaybookLibraryItem = async (id: string): Promise<void> => {
  try {
    await apiService.delete(`/salesplaybook-library/${id}`);
  } catch (error) {
    console.error('[PlaybookLibrary API] Error deleting item:', error);
    throw error;
  }
};

export const uploadPlaybookLibraryExcel = async (
  file: File
): Promise<{ message: string; counts: { pitch: number; question: number; advanced: number } }> => {
  try {
    const formData = new FormData();
    formData.append('file', file);

    const response = await apiService.postFormData<any>('/salesplaybook-library/bulk-upload', formData);
    if (response?.success) {
      return { message: (response as any).message, counts: (response as any).counts };
    }
    return response as any;
  } catch (error) {
    console.error('[PlaybookLibrary API] Error uploading Excel:', error);
    throw error;
  }
};

export const downloadPlaybookLibraryTemplate = async (): Promise<void> => {
  try {
    const token = await getValidToken();
    const url = `${apiConfig.baseURL}/api/salesplaybook-library/template`;
    const response = await fetch(url, {
      headers: { Authorization: `Bearer ${token}` }
    });

    if (!response.ok) {
      throw new Error('Failed to download template');
    }

    const blob = await response.blob();
    const downloadUrl = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = downloadUrl;
    link.download = 'playbook_library_template.xlsx';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(downloadUrl);
  } catch (error) {
    console.error('[PlaybookLibrary API] Error downloading template:', error);
    throw error;
  }
};

// ============================================
// Playbook Ingestion (AI draft review) Functions
// ============================================

export type PlaybookIngestionMode = 'upload' | 'generate' | 'manual';

export type PlaybookIngestionStatus =
  | 'pending'
  | 'locking_protected_content'
  | 'extracting'
  | 'generating'
  | 'cleaning'
  | 'ready_for_review'
  | 'synced'
  | 'error';

export interface PlaybookIngestionProgress {
  currentStep: 'locking_protected_content' | 'extracting' | 'generating' | 'cleaning' | null;
  percentage: number;
  message?: string;
}

export interface PlaybookIngestionError {
  message: string;
  step?: string | null;
  timestamp?: string;
  retryCount?: number;
}

export interface PlaybookIngestionItemError {
  stage?: string;
  roleName?: string;
  section?: 'pitch' | 'advanced' | 'questions' | null;
  index?: number | null;
  message?: string;
  timestamp?: string;
}

export interface PlaybookIngestionProtectedSpan {
  text: string;
  reason: 'compliance_disclosure' | 'approved_claim' | 'official_product_name' | 'can_say_cannot_say' | 'other';
}

export interface PlaybookIngestionDraftBullet {
  text: string;
  sortOrder: number;
}

export interface PlaybookIngestionDraftQuestion {
  questionText: string;
  tags: SalesPlaybookTag[];
  sortOrder: number;
  isActive: boolean;
}

export interface PlaybookIngestionDraftRole {
  name: string;
  displayName: string;
  sortOrder: number;
  isActive: boolean;
  pitch: PlaybookIngestionDraftBullet[];
  advanced: PlaybookIngestionDraftBullet[];
  questions: PlaybookIngestionDraftQuestion[];
}

export interface PlaybookIngestionTask {
  _id: string;
  sourceKnowledgeTaskId?: string | null;
  mode: PlaybookIngestionMode;
  sourceText?: string;
  sourceDocumentName?: string;
  hints?: { audiences?: string[]; targetCustomerHint?: string; dealSizeHint: string };
  createdBy?: string;
  createdByEmail?: string;
  status: PlaybookIngestionStatus;
  styleCleanupStatus?: 'pending' | 'cleaned' | 'manual' | 'error' | null;
  styleCleanedAt?: string | null;
  protectedContentStatus?: 'pending' | 'verified' | 'not_applicable' | 'error' | null;
  protectedContentVerifiedAt?: string | null;
  progress: PlaybookIngestionProgress;
  protectedSpans: PlaybookIngestionProtectedSpan[];
  draftPlaybookName: string;
  draftPlaybookDisplayName: string;
  draftRoles: PlaybookIngestionDraftRole[];
  itemErrors: PlaybookIngestionItemError[];
  error?: PlaybookIngestionError | null;
  syncedPlaybookName?: string;
  syncedRoleIds?: string[];
  syncedAt?: string;
  createdAt: string;
  updatedAt: string;
  completedAt?: string;
}

export interface PlaybookIngestionTasksResponse {
  data: PlaybookIngestionTask[];
  pagination: { page: number; limit: number; total: number; pages: number };
}

export const replaceKnowledgeGeneratorStarterPlaybookFromUpload = async (
  knowledgeTaskId: string,
  sourceText: string,
  sourceDocumentName?: string
): Promise<PlaybookIngestionTask> => {
  const response = await apiService.post<any>(
    `/knowledge-generator/tasks/${knowledgeTaskId}/onboarding-draft/starter-playbook/upload`,
    { sourceText, sourceDocumentName }
  ) as any;
  if (response?.success && response?.data) return response.data;
  throw new Error(response?.error || 'Failed to replace starter playbook draft');
};

export const replaceKnowledgeGeneratorStarterPlaybookWithManual = async (
  knowledgeTaskId: string,
  playbookName?: string
): Promise<PlaybookIngestionTask> => {
  const response = await apiService.post<any>(
    `/knowledge-generator/tasks/${knowledgeTaskId}/onboarding-draft/starter-playbook/manual`,
    { playbookName }
  ) as any;
  if (response?.success && response?.data) return response.data;
  throw new Error(response?.error || 'Failed to start an empty starter playbook draft');
};

export interface PlaybookIngestionSyncResult {
  _id: string;
  status: PlaybookIngestionStatus;
  playbookName: string;
  playbookDisplayName: string;
  salesPlaybookId: string;
  addedRoleIds: string[];
}

// Create a new playbook ingestion task (mode: 'upload' needs sourceText;
// mode: 'generate' takes the two optional hint strings)
export const createPlaybookIngestionTask = async (params: {
  mode: PlaybookIngestionMode;
  sourceText?: string;
  sourceDocumentName?: string;
  targetCustomerHint?: string;
  audiences?: string[];
  dealSizeHint?: string;
  playbookName?: string;
}): Promise<PlaybookIngestionTask> => {
  try {
    const response = await apiService.post<any>('/playbook-ingestion/tasks', params) as any;
    if (response?.success && response?.data) {
      return response.data;
    }
    throw new Error(response?.error || 'Failed to create task');
  } catch (error) {
    console.error('[PlaybookIngestion API] Error creating task:', error);
    throw error;
  }
};

export const getPlaybookAudienceContext = async (): Promise<{ targetAudience: string; playbookAudiences: string[] }> => {
  const response = await apiService.get<any>('/settings/organization') as any;
  return {
    targetAudience: response?.data?.targetAudience || '',
    playbookAudiences: response?.data?.playbookAudiences || [],
  };
};

// Get all playbook ingestion tasks (paginated, list view — excludes large fields)
export const getPlaybookIngestionTasks = async (
  page: number = 1,
  limit: number = 20,
  status?: string,
  sourceKnowledgeTaskId?: string
): Promise<PlaybookIngestionTasksResponse> => {
  try {
    const params: Record<string, string> = {
      page: page.toString(),
      limit: limit.toString()
    };
    if (status) {
      params.status = status;
    }
    if (sourceKnowledgeTaskId) {
      params.sourceKnowledgeTaskId = sourceKnowledgeTaskId;
    }
    const response = await apiService.get<any>('/playbook-ingestion/tasks', params) as any;
    if (response?.success) {
      return {
        data: response.data || [],
        pagination: response.pagination || { page: 1, limit: 20, total: 0, pages: 1 }
      };
    }
    return { data: [], pagination: { page: 1, limit: 20, total: 0, pages: 1 } };
  } catch (error) {
    console.error('[PlaybookIngestion API] Error getting tasks:', error);
    throw error;
  }
};

// Get single playbook ingestion task (full document — used for polling)
export const getPlaybookIngestionTask = async (taskId: string): Promise<PlaybookIngestionTask> => {
  try {
    const response = await apiService.get<any>(`/playbook-ingestion/tasks/${taskId}`) as any;
    if (response?.success && response?.data) {
      return response.data;
    }
    throw new Error('Task not found');
  } catch (error) {
    console.error('[PlaybookIngestion API] Error getting task:', error);
    throw error;
  }
};

// Retry a failed playbook ingestion task
export const retryPlaybookIngestionTask = async (taskId: string): Promise<{ _id: string; status: string; retryCount: number }> => {
  try {
    const response = await apiService.post<any>(`/playbook-ingestion/tasks/${taskId}/retry`) as any;
    if (response?.success && response?.data) {
      return response.data;
    }
    throw new Error(response?.error || 'Failed to retry task');
  } catch (error) {
    console.error('[PlaybookIngestion API] Error retrying task:', error);
    throw error;
  }
};

// Delete/discard a playbook ingestion task
export const deletePlaybookIngestionTask = async (taskId: string): Promise<void> => {
  try {
    const response = await apiService.delete<any>(`/playbook-ingestion/tasks/${taskId}`) as any;
    if (!response?.success) {
      throw new Error(response?.error || 'Failed to delete task');
    }
  } catch (error) {
    console.error('[PlaybookIngestion API] Error deleting task:', error);
    throw error;
  }
};

// Approve & sync a ready_for_review draft into a real SalesPlaybook. Pass
// `override` with the reviewer-edited draft (any subset of the three
// fields) to save edits made in the review UI before approving; omit it to
// sync the draft exactly as the pipeline produced it.
export const syncPlaybookIngestionTask = async (
  taskId: string,
  override?: {
    draftPlaybookName?: string;
    draftPlaybookDisplayName?: string;
    draftRoles?: PlaybookIngestionDraftRole[];
    conflictMode?: 'merge' | 'replace' | 'create_new';
    createNewDisplayName?: string;
  }
): Promise<PlaybookIngestionSyncResult> => {
  try {
    const response = await apiService.post<any>(`/playbook-ingestion/tasks/${taskId}/sync`, override || {}) as any;
    if (response?.success && response?.data) {
      return response.data;
    }
    throw new Error(response?.error || 'Failed to sync task');
  } catch (error) {
    console.error('[PlaybookIngestion API] Error syncing task:', error);
    throw error;
  }
};

// Save reviewer edits to an unpublished playbook draft. This endpoint never
// creates or updates a live SalesPlaybook; onboarding publication is owned by
// the final starter-set action.
export const savePlaybookIngestionDraft = async (
  taskId: string,
  draft: {
    expectedUpdatedAt: string;
    draftPlaybookName?: string;
    draftPlaybookDisplayName: string;
    draftRoles: PlaybookIngestionDraftRole[];
  }
): Promise<PlaybookIngestionTask> => {
  const response = await apiService.patch<any>(`/playbook-ingestion/tasks/${taskId}/draft`, draft) as any;
  if (response?.success && response?.data) return response.data;
  throw new Error(response?.error || 'Failed to save playbook draft');
};

// Backward compatibility aliases
export const getTalkTrackTags = getSalesPlaybookTags;
export const getTalkTrackPlaybookOptions = getSalesPlaybookOptions;
export const getTalkTracks = getSalesPlaybooks;
export const getTalkTrackByName = getSalesPlaybookByName;
export const createTalkTrack = createSalesPlaybook;
export const updateTalkTrack = updateSalesPlaybook;
export const deleteTalkTrack = deleteSalesPlaybook;
export const toggleTalkTrackStatus = toggleSalesPlaybookStatus;
export const addTalkTrackRole = addSalesPlaybookRole;
export const updateTalkTrackRole = updateSalesPlaybookRole;
export const deleteTalkTrackRole = deleteSalesPlaybookRole;
export const addTalkTrackPitchBullet = addSalesPlaybookPitchBullet;
export const updateTalkTrackPitchBullet = updateSalesPlaybookPitchBullet;
export const deleteTalkTrackPitchBullet = deleteSalesPlaybookPitchBullet;
export const addTalkTrackQuestion = addSalesPlaybookQuestion;
export const updateTalkTrackQuestion = updateSalesPlaybookQuestion;
export const deleteTalkTrackQuestion = deleteSalesPlaybookQuestion;


// ============================================
// Call Insights Functions
// ============================================

export const updateCallInsightsReview = async (
  sessionId: string,
  reviewData: { overall_comment: string; section_comments: Record<string, string> }
): Promise<ApiResponse> => {
  try {
    return await apiService.patch(`/admin/sessions/${sessionId}/call-insights-review`, reviewData);
  } catch (error) {
    console.error('[CallInsightsAPI] Error saving manager review:', error);
    throw error;
  }
};

export const triggerCallInsightsReanalysis = async (sessionId: string): Promise<ApiResponse> => {
  try {
    return await apiService.post(`/admin/sessions/${sessionId}/call-insights-reanalyze`);
  } catch (error) {
    console.error('[CallInsightsAPI] Error triggering reanalysis:', error);
    throw error;
  }
};

export default apiService;
